import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiBarChart2,
    FiCreditCard,
    FiDollarSign,
    FiPlusCircle,
    FiPrinter,
    FiRefreshCcw,
    FiSidebar,
    FiShoppingBag,
    FiUnlock,
    FiLock,
    FiMinus,
    FiPlus,
    FiTrash2,
    FiX,
    FiCheckCircle,
    FiXCircle,
    FiMaximize2,
    FiVolume2,
    FiVolumeX,
    FiFilter,
} from 'react-icons/fi';
import './PosPage.css';
import { TabButton } from './TabButton';
import { PaymentModal } from '../../components/PaymentModal';
import { apiFetch } from '../../api/client';
import logger from '../../utils/logger';
import {
    CashSessionDTO,
    CashflowManualEntryPayload,
    CashflowSummaryResponse,
    CashflowSummaryParams,
    closeCashSession,
    fetchCashflowSummary,
    listCashSessions,
    openCashSession,
    recordManualPayment,
} from '../../api/cashflowApi';
import { useFeatureEnabled } from '../../components/FeatureGate';
import {
    fetchPosDashboard,
    fetchPosOrderDetail,
    cancelPosOrder,
    PosDashboard,
    PosOrderDetail,
    PosOrderPreview,
    PosTable,
    PosMenuItem,
    PosMenuCategory,
    PosMenuVariant,
    PosOrderPaymentPayload,
    registerPosOrderPayment,
    PosOrderInvoiceLink,
    fetchPosOrderInvoice,
    linkPosOrderCompany,
    issuePosOrderInvoice,
    createPosOrder,
    CreatePosOrderPayload,
    deletePosTable,
} from '../../api/posApi';
import { CompanyDirectoryEntry, listCompanyDirectory } from '../../api/companyDirectoryApi';

const currencyFormat = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const toNumber = (value?: number | string | null) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

const formatCurrency = (value?: number | string | null) => currencyFormat.format(toNumber(value));

const formatTime = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const roundCurrency = (value: number) => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Number(value.toFixed(2));
};

const buildVariantKey = (variant: PosMenuVariant | null | undefined): string => {
    if (!variant) {
        return 'variant:unknown';
    }
    if (variant.id !== null && variant.id !== undefined) {
        return `id:${variant.id}`;
    }
    const normalizedName = (variant.name ?? '').trim().toLowerCase();
    const priceToken = roundCurrency(toNumber(variant.price));
    return `name:${normalizedName}|price:${priceToken}`;
};

const clampQuantity = (value: number) => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(Math.max(Math.round(value), 0), 99);
};

const extractSnapshotString = (snapshot: Record<string, unknown> | null | undefined, keys: string[]): string | null => {
    if (!snapshot) {
        return null;
    }
    for (const key of keys) {
        const value = snapshot[key];
        if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim();
        }
    }
    return null;
};

const normalizeTableKey = (value: string | number | null | undefined): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const raw = String(value).trim();
    if (raw.length === 0) {
        return null;
    }
    const numeric = Number.parseInt(raw, 10);
    if (Number.isFinite(numeric)) {
        return `n-${numeric}`;
    }
    return `s-${raw.toLowerCase()}`;
};

const sortByTableLabel = (a: string, b: string) => {
    const parse = (label: string) => {
        const trimmed = label.trim();
        const numeric = Number.parseInt(trimmed, 10);
        if (Number.isFinite(numeric)) {
            return { kind: 'number' as const, value: numeric };
        }
        return { kind: 'string' as const, value: trimmed.toLowerCase() };
    };
    const parsedA = parse(a);
    const parsedB = parse(b);
    if (parsedA.kind === 'number' && parsedB.kind === 'number') {
        return parsedA.value - parsedB.value;
    }
    if (parsedA.kind === 'number') {
        return -1;
    }
    if (parsedB.kind === 'number') {
        return 1;
    }
    if (parsedA.value < parsedB.value) {
        return -1;
    }
    if (parsedA.value > parsedB.value) {
        return 1;
    }
    return 0;
};

const todayRange = () => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: now.toISOString() };
};

const formatDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const startOfDayIso = (date: Date) => {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy.toISOString();
};

const endOfDayIso = (date: Date) => {
    const copy = new Date(date);
    copy.setHours(23, 59, 59, 999);
    return copy.toISOString();
};

const formatDateTimeCompact = (iso?: string | null) => {
    if (!iso) {
        return '—';
    }
    try {
        const date = new Date(iso);
        return date.toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

const formatIntervalLabel = (from?: string | null, to?: string | null) => {
    if (!from || !to) {
        return 'Intervallo non disponibile';
    }
    return `${formatDateTimeCompact(from)} → ${formatDateTimeCompact(to)}`;
};

const buildFilenameTimestamp = () => new Date().toISOString().replace(/[:]/g, '-').replace('T', '-').split('.')[0];

type SummaryFilterMode = 'today' | 'activeSession' | 'session' | 'range';

type SummaryConfig = {
    params: CashflowSummaryParams;
    title: string;
    subtitle: string;
    notice?: string;
    sessionId?: number | null;
};

type ManualModalConfig = {
    method: 'CASH' | 'POS' | 'CUSTOM';
    title: string;
    description: string;
};

type BannerState = { type: 'success' | 'error'; message: string } | null;
type PaymentConfirmationState = {
    title: string;
    subtitle?: string;
    orderLabel?: string;
    changeLabel?: string;
};

type TableCardVariant = 'free' | 'occupied' | 'closed';

const TableIllustration: React.FC<{ variant: TableCardVariant }> = ({ variant }) => {
    const accent = variant === 'occupied' ? '#F97316' : variant === 'closed' ? '#6366F1' : '#22C55E';
    return (
        <svg className="pos-table-card__svg" viewBox="0 0 120 80" role="presentation" aria-hidden="true">
            <circle cx="42" cy="40" r="30" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="4" />
            <circle cx="42" cy="40" r="18" fill="#ffffff" stroke="#e5e7eb" strokeWidth="2" />
            <path d="M42 11 A29 29 0 0 1 71 40 L42 40 Z" fill={accent} opacity="0.85" />
            <rect x="86" y="16" width="6" height="48" rx="3" fill="#4b5563" />
            <rect x="96" y="16" width="4" height="48" rx="2" fill="#9ca3af" />
            <rect x="100" y="16" width="4" height="48" rx="2" fill="#374151" />
            <rect x="81" y="16" width="3" height="48" rx="1.5" fill="#9ca3af" />
        </svg>
    );
};

type DraftItem = {
    key: string;
    sourceId?: number | string | null;
    name: string;
    price: number;
    quantity: number;
    isCustom?: boolean;
    baseName?: string;
    basePrice?: number;
    isVariant?: boolean;
    parentKey?: string | null;
    variantId?: number | null;
    variantKey?: string | null;
    variantName?: string | null;
};

type AdjustmentFormState = {
    customTotal: string;
    discountMode: 'amount' | 'percentage';
    discountValue: string;
    voucherCount: string;
    voucherValue: string;
    splitShares: string;
};

const buildDefaultAdjustmentForm = (): AdjustmentFormState => ({
    customTotal: '',
    discountMode: 'amount',
    discountValue: '',
    voucherCount: '',
    voucherValue: '',
    splitShares: '',
});

type AdjustmentModalType = 'customTotal' | 'discount' | 'voucher' | 'split';

const ADJUSTMENT_TAB_META: Record<AdjustmentModalType, { label: string; description: string }> = {
    customTotal: {
        label: 'Prezzo personalizzato',
        description: 'Imposta manualmente il totale finale per adeguare il conto al desiderato.',
    },
    discount: {
        label: 'Sconti',
        description: 'Applica uno sconto in valore assoluto o percentuale sull’importo corrente.',
    },
    voucher: {
        label: 'Buoni pasto',
        description: 'Indica quantità e valore dei buoni pasto riscossi dal cliente.',
    },
    split: {
        label: 'Dividi conto',
        description: 'Calcola la quota per persona suddividendo il totale tra più coperti.',
    },
};

const ADJUSTMENT_TABS: AdjustmentModalType[] = ['customTotal', 'discount', 'voucher', 'split'];

type CategoryTheme = {
    headerGradient: string;
    headerText: string;
    itemBackground: string;
    itemHoverBackground: string;
    itemText: string;
    itemPrice: string;
    laneBorder: string;
    badgeBackground: string;
    badgeText: string;
};

const CATEGORY_THEME_PRESETS: CategoryTheme[] = [
    {
        headerGradient: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
        headerText: '#f8fafc',
        itemBackground: 'linear-gradient(135deg, rgba(14, 165, 233, 0.22), rgba(37, 99, 235, 0.35))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(14, 165, 233, 0.32), rgba(37, 99, 235, 0.55))',
        itemText: '#0f172a',
        itemPrice: '#1d4ed8',
        laneBorder: 'rgba(37, 99, 235, 0.28)',
        badgeBackground: 'rgba(14, 165, 233, 0.2)',
        badgeText: '#0f172a',
    },
    {
        headerGradient: 'linear-gradient(135deg, #f97316, #ef4444)',
        headerText: '#fff7ed',
        itemBackground: 'linear-gradient(135deg, rgba(249, 115, 22, 0.22), rgba(239, 68, 68, 0.36))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(249, 115, 22, 0.35), rgba(239, 68, 68, 0.55))',
        itemText: '#451a03',
        itemPrice: '#c2410c',
        laneBorder: 'rgba(234, 88, 12, 0.35)',
        badgeBackground: 'rgba(249, 115, 22, 0.18)',
        badgeText: '#7c2d12',
    },
    {
        headerGradient: 'linear-gradient(135deg, #22c55e, #10b981)',
        headerText: '#ecfdf5',
        itemBackground: 'linear-gradient(135deg, rgba(13, 148, 136, 0.18), rgba(22, 163, 74, 0.32))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(13, 148, 136, 0.32), rgba(22, 163, 74, 0.48))',
        itemText: '#064e3b',
        itemPrice: '#047857',
        laneBorder: 'rgba(16, 185, 129, 0.28)',
        badgeBackground: 'rgba(16, 185, 129, 0.18)',
        badgeText: '#065f46',
    },
    {
        headerGradient: 'linear-gradient(135deg, #9333ea, #6366f1)',
        headerText: '#f3e8ff',
        itemBackground: 'linear-gradient(135deg, rgba(147, 51, 234, 0.22), rgba(99, 102, 241, 0.36))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(147, 51, 234, 0.38), rgba(99, 102, 241, 0.55))',
        itemText: '#312e81',
        itemPrice: '#6d28d9',
        laneBorder: 'rgba(99, 102, 241, 0.32)',
        badgeBackground: 'rgba(99, 102, 241, 0.2)',
        badgeText: '#312e81',
    },
    {
        headerGradient: 'linear-gradient(135deg, #f59e0b, #facc15)',
        headerText: '#fff7e6',
        itemBackground: 'linear-gradient(135deg, rgba(245, 158, 11, 0.22), rgba(250, 204, 21, 0.32))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(245, 158, 11, 0.32), rgba(250, 204, 21, 0.48))',
        itemText: '#78350f',
        itemPrice: '#b45309',
        laneBorder: 'rgba(251, 191, 36, 0.35)',
        badgeBackground: 'rgba(251, 191, 36, 0.2)',
        badgeText: '#7c2d12',
    },
    {
        headerGradient: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
        headerText: '#ecfeff',
        itemBackground: 'linear-gradient(135deg, rgba(20, 184, 166, 0.2), rgba(6, 182, 212, 0.3))',
        itemHoverBackground: 'linear-gradient(135deg, rgba(20, 184, 166, 0.32), rgba(6, 182, 212, 0.48))',
        itemText: '#0f172a',
        itemPrice: '#0e7490',
        laneBorder: 'rgba(6, 182, 212, 0.3)',
        badgeBackground: 'rgba(6, 182, 212, 0.24)',
        badgeText: '#0f172a',
    },
];

const INTERNAL_TABLE_NUMBERS = Array.from({ length: 20 }, (_, index) => String(index + 1));
const EXTERNAL_TABLE_NUMBERS = Array.from({ length: 25 }, (_, index) => String(101 + index));

type TableGroupDefinition = {
    key: string;
    label: string;
    numbers: string[];
};

const DEFAULT_TABLE_GROUPS: TableGroupDefinition[] = [
    { key: 'internal', label: 'Tavoli interni', numbers: INTERNAL_TABLE_NUMBERS },
    { key: 'external', label: 'Tavoli esterni', numbers: EXTERNAL_TABLE_NUMBERS },
];

const CATEGORY_THEME_MATCHERS: { pattern: RegExp; presetIndex: number }[] = [
    { pattern: /(caff|coffee|bar)/i, presetIndex: 0 },
    { pattern: /(bib|drink|bev|bevanda)/i, presetIndex: 5 },
    { pattern: /(dolc|dessert|pastic|sweet)/i, presetIndex: 4 },
    { pattern: /(pizza|panin|primi|piatti|food)/i, presetIndex: 1 },
    { pattern: /(vino|cocktail|spritz|aper)/i, presetIndex: 3 },
];

const resolveCategoryTheme = (name: string | null | undefined, index: number): CategoryTheme => {
    const normalized = (name ?? '').toLowerCase();
    const matched = CATEGORY_THEME_MATCHERS.find(({ pattern }) => pattern.test(normalized));
    const presetIndex = matched ? matched.presetIndex : index % CATEGORY_THEME_PRESETS.length;
    return CATEGORY_THEME_PRESETS[presetIndex] ?? CATEGORY_THEME_PRESETS[0];
};

type CategoryOption = {
    key: string;
    label: string;
    description: string | null;
    items: PosMenuItem[];
    theme: CategoryTheme;
    raw: PosMenuCategory;
};

type CatalogItem = PosMenuItem & { __category: CategoryOption };

const PosPageFull: React.FC = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<CashSessionDTO[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState<boolean>(true);
    const [summary, setSummary] = useState<CashflowSummaryResponse | null>(null);
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const [summaryFilterMode, setSummaryFilterMode] = useState<SummaryFilterMode>('today');
    const [summaryFilterSessionId, setSummaryFilterSessionId] = useState<number | null>(null);
    const [summaryRangeFrom, setSummaryRangeFrom] = useState<string>(() => formatDateInputValue(new Date()));
    const [summaryRangeTo, setSummaryRangeTo] = useState<string>(() => formatDateInputValue(new Date()));
    const [manualModal, setManualModal] = useState<ManualModalConfig | null>(null);
    const [manualForm, setManualForm] = useState({ amount: '', note: '', reference: '' });
    const [manualSubmitting, setManualSubmitting] = useState(false);
    const [manualError, setManualError] = useState<string | null>(null);
    const [banner, setBanner] = useState<BannerState>(null);
    const [busy, setBusy] = useState(false);
    const [dashboard, setDashboard] = useState<PosDashboard | null>(null);
    const [dashboardLoading, setDashboardLoading] = useState<boolean>(true);
    const [dashboardError, setDashboardError] = useState<string | null>(null);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<PosOrderDetail | null>(null);
    const [orderLoading, setOrderLoading] = useState<boolean>(false);
    const [orderError, setOrderError] = useState<string | null>(null);
    const [paymentInput, setPaymentInput] = useState<string>('');
    const [productSearch, setProductSearch] = useState<string>('');
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
    const [catalogPanelOpen, setCatalogPanelOpen] = useState<boolean>(false);
    const [draftItems, setDraftItems] = useState<DraftItem[]>([]);
    const [orderAdjustments, setOrderAdjustments] = useState<Record<string, number>>({});
    const [discount, setDiscount] = useState<{ type: 'amount' | 'percentage'; value: number } | null>(null);
    const [mealVoucher, setMealVoucher] = useState<{ count: number; value: number } | null>(null);
    const [splitInfo, setSplitInfo] = useState<{ shares: number } | null>(null);
    const [orderSubmitting, setOrderSubmitting] = useState(false);
    const [adjustmentModal, setAdjustmentModal] = useState<AdjustmentModalType | null>(null);
    const [adjustmentForm, setAdjustmentForm] = useState<AdjustmentFormState>(() => buildDefaultAdjustmentForm());
    const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [pendingPaymentAction, setPendingPaymentAction] = useState<'cash' | 'pos' | null>(null);
    const [paymentConfirmation, setPaymentConfirmation] = useState<PaymentConfirmationState | null>(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [customItemModalOpen, setCustomItemModalOpen] = useState(false);
    const [customItemForm, setCustomItemForm] = useState<{ name: string; price: string; quantity: string }>({
        name: '',
        price: '',
        quantity: '1',
    });
    const [customItemError, setCustomItemError] = useState<string | null>(null);
    const [invoiceInfo, setInvoiceInfo] = useState<PosOrderInvoiceLink | null>(null);
    const [variantDraftSelections, setVariantDraftSelections] = useState<Record<string, string>>({});
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceError, setInvoiceError] = useState<string | null>(null);
    const [invoicePanelOpen, setInvoicePanelOpen] = useState(false);
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
    const [companySearch, setCompanySearch] = useState('');
    const [companyResults, setCompanyResults] = useState<CompanyDirectoryEntry[]>([]);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyError, setCompanyError] = useState<string | null>(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    const [showSessionPanel, setShowSessionPanel] = useState(false);
    const [showStatsPanel, setShowStatsPanel] = useState(false);
    const [showQuickActions, setShowQuickActions] = useState(false);
    const [tableGroupFilter, setTableGroupFilter] = useState<string>('all');
    const [tableStatusFilter, setTableStatusFilter] = useState<'all' | 'free' | 'occupied' | 'checkout'>('all');
    const [showStatusFilterDropdown, setShowStatusFilterDropdown] = useState(false);
    const [showKeypad, setShowKeypad] = useState(false);
    const [keypadMode, setKeypadMode] = useState<'cash' | 'card' | 'custom' | null>(null);

    // ============ TAB DINAMICHE (Tavoli / Ordini) ============
    const [activeTab, setActiveTab] = useState<'tables' | 'orders' | 'counter'>('tables');
    // =========================================================

    const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
        const stored = localStorage.getItem('pos-sound-enabled');
        return stored ? stored === 'true' : true;
    });
    const audioContextRef = React.useRef<AudioContext | null>(null);
    const previousCheckoutOrderIdsRef = React.useRef<Set<number>>(new Set());
    const paymentConfirmationTimeoutRef = React.useRef<number | null>(null);

    const dismissPaymentConfirmation = useCallback(() => {
        if (paymentConfirmationTimeoutRef.current !== null) {
            window.clearTimeout(paymentConfirmationTimeoutRef.current);
            paymentConfirmationTimeoutRef.current = null;
        }
        setPaymentConfirmation(null);
    }, []);

    const showPaymentConfirmation = useCallback((confirmation: PaymentConfirmationState) => {
        if (paymentConfirmationTimeoutRef.current !== null) {
            window.clearTimeout(paymentConfirmationTimeoutRef.current);
        }
        setPaymentConfirmation(confirmation);
        paymentConfirmationTimeoutRef.current = window.setTimeout(() => {
            setPaymentConfirmation(null);
            paymentConfirmationTimeoutRef.current = null;
        }, 4200);
    }, []);

    useEffect(() => () => {
        if (paymentConfirmationTimeoutRef.current !== null) {
            window.clearTimeout(paymentConfirmationTimeoutRef.current);
        }
    }, []);
    const [priceMode, setPriceMode] = useState<'counter' | 'table'>(() => {
        const stored = localStorage.getItem('pos-price-mode');
        return (stored === 'table' || stored === 'counter') ? stored : 'counter';
    });
    const [selectedTableNumber, setSelectedTableNumber] = useState<string | null>(null);

    // 🍽️ Coperto Modal
    const [coverChargeModalOpen, setCoverChargeModalOpen] = useState(false);
    const [coverChargeAmount, setCoverChargeAmount] = useState<number>(0);
    const [numberOfPeople, setNumberOfPeople] = useState<string>('2');

    const orderBoardEnabled = useFeatureEnabled('orderBoardEnabled');
    const printCenterEnabled = useFeatureEnabled('printCenterEnabled');
    const cashflowEnabled = useFeatureEnabled('cashflowEnabled');

    useEffect(() => {
        setVariantDraftSelections(prev => {
            const allowedKeys = new Set(draftItems.filter(item => !item.isVariant).map(item => item.key));
            let changed = false;
            const next: Record<string, string> = {};

            for (const [key, value] of Object.entries(prev)) {
                if (allowedKeys.has(key)) {
                    next[key] = value;
                } else {
                    changed = true;
                }
            }

            for (const key of allowedKeys) {
                if (!(key in next)) {
                    next[key] = '';
                    changed = true;
                }
            }

            return changed ? next : prev;
        });
    }, [draftItems]);

    const activeSession = useMemo(() => sessions.find(session => session.open), [sessions]);

    const resolveSummaryConfig = useCallback((): SummaryConfig => {
        const baseRange = todayRange();
        const baseConfig: SummaryConfig = {
            params: {
                from: baseRange.from,
                to: baseRange.to,
                granularity: 'DAY',
            },
            title: 'Incassi del giorno',
            subtitle: formatIntervalLabel(baseRange.from, baseRange.to),
        };

        if (summaryFilterMode === 'today') {
            return baseConfig;
        }

        if (summaryFilterMode === 'activeSession') {
            if (activeSession?.openedAt) {
                const to = activeSession.closedAt ?? new Date().toISOString();
                return {
                    params: {
                        from: activeSession.openedAt,
                        to,
                        granularity: 'SESSION',
                    },
                    title: 'Sessione attiva',
                    subtitle: formatIntervalLabel(activeSession.openedAt, to),
                    sessionId: activeSession.id,
                    notice: activeSession.closedAt ? undefined : 'Sessione in corso: i totali includono i movimenti più recenti.',
                };
            }
            return { ...baseConfig, notice: 'Nessuna sessione attiva, mostrati gli incassi odierni.' };
        }

        if (summaryFilterMode === 'session') {
            if (summaryFilterSessionId != null) {
                const matched = sessions.find(session => session.id === summaryFilterSessionId);
                if (matched?.openedAt) {
                    const to = matched.closedAt ?? new Date().toISOString();
                    return {
                        params: {
                            from: matched.openedAt,
                            to,
                            granularity: 'SESSION',
                        },
                        title: `Sessione #${matched.id}`,
                        subtitle: formatIntervalLabel(matched.openedAt, to),
                        sessionId: matched.id,
                        notice: matched.open ? 'Sessione non ancora chiusa.' : undefined,
                    };
                }
                return { ...baseConfig, notice: 'Sessione selezionata non trovata, mostrati gli incassi odierni.' };
            }
            return { ...baseConfig, notice: 'Seleziona una sessione per applicare il filtro.' };
        }

        if (summaryFilterMode === 'range') {
            if (summaryRangeFrom && summaryRangeTo) {
                const startCandidate = new Date(summaryRangeFrom);
                const endCandidate = new Date(summaryRangeTo);
                const fromDate = startCandidate <= endCandidate ? startCandidate : endCandidate;
                const toDate = startCandidate <= endCandidate ? endCandidate : startCandidate;
                const params: CashflowSummaryParams = {
                    from: startOfDayIso(fromDate),
                    to: endOfDayIso(toDate),
                    granularity: 'DAY',
                };
                return {
                    params,
                    title: 'Intervallo personalizzato',
                    subtitle: formatIntervalLabel(params.from, params.to),
                };
            }
            return { ...baseConfig, notice: 'Imposta un intervallo completo per applicare il filtro.' };
        }

        return baseConfig;
    }, [summaryFilterMode, summaryFilterSessionId, summaryRangeFrom, summaryRangeTo, sessions, activeSession]);

    const loadSessions = useCallback(async () => {
        setSessionsLoading(true);
        try {
            const response = await listCashSessions();
            setSessions(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile recuperare le sessioni di cassa.';
            setBanner({ type: 'error', message });
        } finally {
            setSessionsLoading(false);
        }
    }, []);

    const loadSummary = useCallback(async () => {
        const config = resolveSummaryConfig();
        setSummaryLoading(true);
        setSummaryError(null);
        try {
            const response = await fetchCashflowSummary(config.params);
            setSummary(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile recuperare il riepilogo cassa.';
            setSummaryError(message);
        } finally {
            setSummaryLoading(false);
        }
    }, [resolveSummaryConfig]);

    const summaryConfig = useMemo(() => resolveSummaryConfig(), [resolveSummaryConfig]);

    const loadDashboard = useCallback(async (silent = false) => {
        if (!silent) {
            setDashboardLoading(true);
        }
        setDashboardError(null);
        try {
            const response = await fetchPosDashboard();

            // Detect new checkout requests for sound notification
            const currentCheckoutOrderIds = new Set<number>();
            response.tables?.forEach(table => {
                const order = table.currentOrder;
                if (order && order.status === 'CHECKOUT_REQUESTED') {
                    currentCheckoutOrderIds.add(order.id);
                }
            });

            // Find new checkout requests (not in previous set)
            const newCheckoutOrders = Array.from(currentCheckoutOrderIds).filter(
                id => !previousCheckoutOrderIdsRef.current.has(id)
            );

            // Play sound for new checkout requests
            if (newCheckoutOrders.length > 0 && soundEnabled) {
                playNotificationSound();
            }

            // Update ref with current IDs
            previousCheckoutOrderIdsRef.current = currentCheckoutOrderIds;
            setDashboard(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile recuperare i dati POS.';
            setDashboardError(message);
        } finally {
            if (!silent) {
                setDashboardLoading(false);
            }
        }
    }, [soundEnabled]); const ensureAudioContext = useCallback(() => {
        if (audioContextRef.current) {
            return audioContextRef.current;
        }
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
            console.warn('Web Audio API non supportato in questo browser');
            return null;
        }
        const ctx = new AudioContextClass();
        audioContextRef.current = ctx;
        return ctx;
    }, []);

    const playNotificationSound = useCallback(() => {
        const ctx = ensureAudioContext();
        if (!ctx) {
            return;
        }

        const scheduleBeep = (freq: number, startTime: number, duration: number) => {
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            oscillator.type = 'sine'; // Onda sinusoidale più morbida
            oscillator.frequency.value = freq;
            gainNode.gain.setValueAtTime(0, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.15, startTime + 0.01); // Volume più basso
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
            oscillator.start(startTime);
            oscillator.stop(startTime + duration);
        };

        const now = ctx.currentTime;
        // Suono più piacevole: do medio (523 Hz) e mi (659 Hz)
        scheduleBeep(523, now, 0.2);
        scheduleBeep(659, now + 0.25, 0.2);
    }, [ensureAudioContext]);

    const toggleSound = useCallback(() => {
        const newValue = !soundEnabled;
        setSoundEnabled(newValue);
        localStorage.setItem('pos-sound-enabled', String(newValue));
        if (newValue) {
            playNotificationSound();
        }
    }, [soundEnabled, playNotificationSound]);

    // 🍽️ Carica importo coperto dalle settings
    useEffect(() => {
        const loadCoverCharge = async () => {
            try {
                const response = await apiFetch('/api/tenant-settings');
                if (response.ok) {
                    const settings = await response.json();
                    console.log('🍽️ COPERTO CARICATO:', settings.coverCharge);
                    setCoverChargeAmount(settings.coverCharge || 0);
                } else {
                    console.error('🍽️ Errore caricamento settings:', response.status);
                }
            } catch (error) {
                console.error('Failed to load cover charge settings:', error);
            }
        };
        loadCoverCharge();
    }, []);

    useEffect(() => {
        loadSessions();
        loadDashboard();
    }, [loadSessions, loadDashboard]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        if (summaryFilterMode !== 'session') {
            return;
        }
        if (summaryFilterSessionId != null && !sessions.some(session => session.id === summaryFilterSessionId)) {
            setSummaryFilterSessionId(null);
        }
        if (summaryFilterSessionId == null && sessions.length > 0) {
            const fallback = sessions.find(session => !session.open) ?? sessions[0];
            if (fallback) {
                setSummaryFilterSessionId(fallback.id);
            }
        }
    }, [summaryFilterMode, summaryFilterSessionId, sessions]);

    // Polling silenzioso per aggiornare ordini ogni 30 secondi
    useEffect(() => {
        const interval = setInterval(() => {
            loadDashboard(true); // Silent mode
        }, 30000); // 30 secondi

        return () => clearInterval(interval);
    }, [loadDashboard]);

    // Chiudi dropdown filtro quando si clicca fuori
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showStatusFilterDropdown && !target.closest('.pos-table-filter-wrapper')) {
                setShowStatusFilterDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showStatusFilterDropdown]);

    useEffect(() => {
        if (!banner) {
            return;
        }
        const timeout = window.setTimeout(() => setBanner(null), 4000);
        return () => window.clearTimeout(timeout);
    }, [banner]);

    useEffect(() => {
        if (selectedOrderId == null) {
            setSelectedOrder(null);
            setOrderError(null);
            setOrderLoading(false);
            return;
        }
        let disposed = false;
        setOrderLoading(true);
        setOrderError(null);
        fetchPosOrderDetail(selectedOrderId)
            .then(detail => {
                if (!disposed) {
                    setSelectedOrder(detail);
                }
            })
            .catch(err => {
                if (!disposed) {
                    const message = err instanceof Error ? err.message : 'Impossibile recuperare il dettaglio ordine.';
                    setOrderError(message);
                    setSelectedOrder(null);
                }
            })
            .finally(() => {
                if (!disposed) {
                    setOrderLoading(false);
                }
            });
        return () => {
            disposed = true;
        };
    }, [selectedOrderId]);

    useEffect(() => {
        setPaymentInput('');
        setDraftItems([]);
        setDiscount(null);
        setMealVoucher(null);
        setSplitInfo(null);
        setOrderAdjustments({});
        setOrderSubmitting(false);
        setAdjustmentModal(null);
        setAdjustmentError(null);
        setInvoiceInfo(null);
        setInvoiceLoading(false);
        setInvoiceError(null);
        setInvoicePanelOpen(false);
        setInvoiceSubmitting(false);
        setCompanySearch('');
        setCompanyResults([]);
        setCompanyLoading(false);
        setCompanyError(null);
        setSelectedCompanyId(null);
    }, [selectedOrderId]);

    const refreshInvoiceInfo = useCallback(async (options?: { silent?: boolean }) => {
        const orderId = selectedOrderId;
        if (!orderId) {
            return;
        }
        const showSpinner = !options?.silent;
        if (showSpinner) {
            setInvoiceLoading(true);
        }
        setInvoiceError(null);
        try {
            const info = await fetchPosOrderInvoice(orderId);
            if (selectedOrderId !== orderId) {
                return;
            }
            setInvoiceInfo(info);
        } catch (err) {
            if (selectedOrderId !== orderId) {
                return;
            }
            const message = err instanceof Error ? err.message : 'Impossibile recuperare le informazioni di fatturazione.';
            setInvoiceError(message);
            setInvoiceInfo(null);
        } finally {
            if (selectedOrderId === orderId && showSpinner) {
                setInvoiceLoading(false);
            }
        }
    }, [selectedOrderId]);

    useEffect(() => {
        if (!selectedOrderId) {
            setInvoiceLoading(false);
            return;
        }
        void refreshInvoiceInfo();
    }, [selectedOrderId, refreshInvoiceInfo]);

    useEffect(() => {
        if (invoiceInfo?.companyId) {
            setSelectedCompanyId(invoiceInfo.companyId);
        }
    }, [invoiceInfo]);

    const loadCompanyDirectory = useCallback(async (query?: string) => {
        if (!selectedOrderId) {
            return;
        }
        setCompanyLoading(true);
        setCompanyError(null);
        try {
            const response = await listCompanyDirectory(query);
            setCompanyResults(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile cercare le anagrafiche aziendali.';
            setCompanyError(message);
            setCompanyResults([]);
        } finally {
            setCompanyLoading(false);
        }
    }, [selectedOrderId]);

    useEffect(() => {
        if (!invoicePanelOpen || !selectedOrder) {
            return;
        }
        // Load only once when panel opens, ignore if already loaded or loading
        if (companyResults.length > 0 || companyLoading) {
            return;
        }
        void loadCompanyDirectory(companySearch);
        // Intentionally limited deps to prevent infinite loop - only trigger on panel open
        // eslint-disable-next-line
    }, [invoicePanelOpen, selectedOrder]);

    const toggleInvoicePanel = useCallback(() => {
        if (!selectedOrder) {
            return;
        }
        setInvoicePanelOpen(prev => {
            const next = !prev;
            if (next && companyResults.length === 0 && !companyLoading) {
                void loadCompanyDirectory(companySearch);
            }
            return next;
        });
    }, [selectedOrder, companyResults, companyLoading, loadCompanyDirectory, companySearch]);

    const handleCompanySearch = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void loadCompanyDirectory(companySearch);
    }, [loadCompanyDirectory, companySearch]);

    const handleCompanyReset = useCallback(() => {
        setCompanySearch('');
        void loadCompanyDirectory('');
    }, [loadCompanyDirectory]);

    const handleCompanySelect = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        if (!value) {
            setSelectedCompanyId(null);
            return;
        }
        const parsed = Number(value);
        setSelectedCompanyId(Number.isFinite(parsed) ? parsed : null);
    }, []);

    const handleLinkCompany = useCallback(async () => {
        if (!selectedOrderId || !selectedCompanyId) {
            return;
        }
        setInvoiceSubmitting(true);
        setInvoiceError(null);
        try {
            const response = await linkPosOrderCompany(selectedOrderId, selectedCompanyId);
            setInvoiceInfo(response);
            setBanner({ type: 'success', message: 'Anagrafica collegata all\'ordine.' });
            if (invoicePanelOpen) {
                void refreshInvoiceInfo({ silent: true });
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile collegare l\'azienda all\'ordine.';
            setInvoiceError(message);
        } finally {
            setInvoiceSubmitting(false);
        }
    }, [selectedOrderId, selectedCompanyId, invoicePanelOpen, refreshInvoiceInfo]);

    const handleIssueInvoice = useCallback(async () => {
        if (!selectedOrderId) {
            return;
        }
        setInvoiceSubmitting(true);
        setInvoiceError(null);
        try {
            const response = await issuePosOrderInvoice(selectedOrderId);
            setInvoiceInfo(response);
            setBanner({ type: 'success', message: 'Fattura inviata correttamente.' });
            void refreshInvoiceInfo({ silent: true });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile inviare la fattura.';
            setInvoiceError(message);
        } finally {
            setInvoiceSubmitting(false);
        }
    }, [selectedOrderId, refreshInvoiceInfo]);

    const triggerManualModal = (config: ManualModalConfig) => {
        setManualModal(config);
        setManualForm({ amount: '', note: '', reference: '' });
        setManualError(null);
    };

    const closeManualModal = () => {
        if (manualSubmitting) {
            return;
        }
        setManualModal(null);
        setManualError(null);
    };

    const handleOpenSession = async () => {
        if (busy) {
            return;
        }
        const value = window.prompt('Importo fondo cassa iniziale (facoltativo)', activeSession?.openingFloat ? String(activeSession.openingFloat) : '');
        if (value === null) {
            return;
        }
        let openingFloat: number | undefined;
        if (value.trim().length > 0) {
            const parsed = Number(value.replace(',', '.'));
            if (!Number.isFinite(parsed)) {
                setBanner({ type: 'error', message: 'Valore non valido per il fondo cassa.' });
                return;
            }
            openingFloat = Number(parsed.toFixed(2));
        }
        setBusy(true);
        try {
            await openCashSession(openingFloat !== undefined ? { openingFloat } : {});
            setBanner({ type: 'success', message: 'Sessione cassa aperta correttamente.' });
            await Promise.all([loadSessions(), loadSummary(), loadDashboard()]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile aprire la sessione.';
            setBanner({ type: 'error', message });
        } finally {
            setBusy(false);
        }
    };

    const handleCloseSession = async () => {
        if (busy || !activeSession) {
            return;
        }
        const amountInput = window.prompt('Totale contante alla chiusura (facoltativo)', activeSession.closingCash ? String(activeSession.closingCash) : '');
        if (amountInput === null) {
            return;
        }
        let closingCash: number | undefined;
        if (amountInput.trim().length > 0) {
            const parsed = Number(amountInput.replace(',', '.'));
            if (!Number.isFinite(parsed)) {
                setBanner({ type: 'error', message: 'Totale di chiusura non valido.' });
                return;
            }
            closingCash = Number(parsed.toFixed(2));
        }
        const notes = window.prompt('Note di chiusura (facoltative)', activeSession.closingNotes ?? '');
        setBusy(true);
        try {
            await closeCashSession(activeSession.id, {
                closingCash,
                notes: notes && notes.trim().length > 0 ? notes.trim() : undefined,
            });
            setBanner({ type: 'success', message: 'Sessione chiusa con successo.' });
            await Promise.all([loadSessions(), loadSummary(), loadDashboard()]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile chiudere la sessione.';
            setBanner({ type: 'error', message });
        } finally {
            setBusy(false);
        }
    };

    const handleManualSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!manualModal) {
            return;
        }
        const normalized = manualForm.amount.replace(',', '.').trim();
        const parsed = Number(normalized);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setManualError('Inserisci un importo maggiore di zero.');
            return;
        }
        const amount = Number(parsed.toFixed(2));
        const payload: CashflowManualEntryPayload = {
            amount,
            method: manualModal.method,
            channel: 'POS',
        };
        if (manualForm.note.trim().length > 0) {
            payload.note = manualForm.note.trim();
        }
        if (manualForm.reference.trim().length > 0) {
            payload.reference = manualForm.reference.trim();
        }
        if (activeSession) {
            payload.cashSessionId = activeSession.id;
        }
        setManualSubmitting(true);
        setManualError(null);
        try {
            await recordManualPayment(payload);
            setManualModal(null);
            setBanner({ type: 'success', message: 'Incasso registrato con successo.' });
            await Promise.all([loadSessions(), loadSummary(), loadDashboard()]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile registrare l\'incasso.';
            setManualError(message);
        } finally {
            setManualSubmitting(false);
        }
    };

    const summaryCards = useMemo(() => {
        const total = toNumber(summary?.totalAmount);
        const cash = summary?.amountByMethod ? toNumber(summary.amountByMethod.CASH) : 0;
        const pos = summary?.amountByMethod ? toNumber(summary.amountByMethod.POS) : 0;
        return [
            {
                key: 'total',
                label: 'Totale incassi',
                value: formatCurrency(total),
            },
            {
                key: 'cash',
                label: 'Contanti',
                value: formatCurrency(cash),
            },
            {
                key: 'pos',
                label: 'Carte / POS',
                value: formatCurrency(pos),
            },
        ];
    }, [summary]);

    const corrispettivoData = useMemo(() => {
        const total = roundCurrency(toNumber(summary?.totalAmount));
        const cash = roundCurrency(summary?.amountByMethod ? toNumber(summary.amountByMethod.CASH) : 0);
        const electronic = roundCurrency(Math.max(total - cash, 0));
        const vat = roundCurrency(toNumber(summary?.estimatedVat));
        const payments = Number(summary?.totalCount ?? 0);
        const orders = Number(summary?.ordersCount ?? 0);
        return { total, cash, electronic, vat, payments, orders };
    }, [summary]);

    const handleExportCorrispettivo = useCallback(() => {
        if (!summary) {
            setBanner({ type: 'error', message: 'Nessun dato disponibile per il corrispettivo.' });
            window.setTimeout(() => setBanner(null), 2400);
            return;
        }
        const config = resolveSummaryConfig();
        const generatedAt = new Date().toLocaleString('it-IT');
        const lines = [
            'Corrispettivo POS',
            `Periodo: ${config.subtitle}`,
            config.sessionId ? `Sessione: ${config.sessionId}` : undefined,
            `Generato il: ${generatedAt}`,
            `Totale incassi: ${formatCurrency(corrispettivoData.total)}`,
            `Totale contanti: ${formatCurrency(corrispettivoData.cash)}`,
            `Totale elettronici: ${formatCurrency(corrispettivoData.electronic)}`,
            `Pagamenti registrati: ${corrispettivoData.payments}`,
            `Ordini chiusi: ${corrispettivoData.orders}`,
            `Stima IVA: ${formatCurrency(corrispettivoData.vat)}`,
        ].filter(Boolean).join('\n');
        const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `corrispettivo-${buildFilenameTimestamp()}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setBanner({ type: 'success', message: 'Corrispettivo esportato con successo.' });
        window.setTimeout(() => setBanner(null), 2500);
    }, [summary, resolveSummaryConfig, corrispettivoData, setBanner]);

    const selectableSessions = useMemo(() => {
        return [...sessions].sort((a, b) => {
            const aTime = a.openedAt ? new Date(a.openedAt).getTime() : 0;
            const bTime = b.openedAt ? new Date(b.openedAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [sessions]);

    const dashboardStats = useMemo(() => {
        const todays = dashboard?.todaysTotal != null ? toNumber(dashboard.todaysTotal) : 0;
        const open = dashboard?.openOrders ?? dashboard?.activeOrders?.length ?? 0;
        const generatedAt = dashboard?.generatedAt ? new Date(dashboard.generatedAt).toLocaleTimeString('it-IT') : null;
        return {
            todaysTotal: formatCurrency(todays),
            openOrders: open,
            generatedAt,
        };
    }, [dashboard]);

    const tableGroups = useMemo(() => {
        type TableEntry = {
            key: string;
            table: PosTable;
            displayNumber: string;
            isPlaceholder: boolean;
        };
        const tables = dashboard?.tables ?? [];
        const actualTables = new Map<string, PosTable>();
        const unnamedTables: PosTable[] = [];
        tables.forEach(table => {
            const key = normalizeTableKey(table.tableNumber);
            if (key) {
                if (!actualTables.has(key)) {
                    actualTables.set(key, table);
                }
            } else {
                unnamedTables.push(table);
            }
        });

        const groups = DEFAULT_TABLE_GROUPS.map<TableGroupDefinition & { entries: TableEntry[] }>(definition => {
            const entries = definition.numbers.map(number => {
                const normalized = normalizeTableKey(number);
                let matched: PosTable | null = null;
                if (normalized && actualTables.has(normalized)) {
                    matched = actualTables.get(normalized) ?? null;
                    actualTables.delete(normalized);
                }
                const resolvedTable: PosTable = matched ?? {
                    tableNumber: number,
                    status: null,
                    occupied: false,
                    currentOrder: null,
                };
                return {
                    key: `${definition.key}-${number}`,
                    table: resolvedTable,
                    displayNumber: matched?.tableNumber?.trim().length ? matched.tableNumber : number,
                    isPlaceholder: matched == null,
                } satisfies TableEntry;
            });
            return { ...definition, entries };
        });

        const remainingTables = [...actualTables.values(), ...unnamedTables];
        if (remainingTables.length > 0) {
            remainingTables.sort((a, b) => sortByTableLabel(a.tableNumber ?? '', b.tableNumber ?? ''));
            groups.push({
                key: 'others',
                label: 'Altri tavoli',
                numbers: [],
                entries: remainingTables.map((table, index) => ({
                    key: `others-${normalizeTableKey(table.tableNumber) ?? index}`,
                    table,
                    displayNumber: table.tableNumber?.trim().length ? table.tableNumber : `Tavolo ${index + 1}`,
                    isPlaceholder: false,
                } satisfies TableEntry)),
            });
        }

        return groups.map(group => ({
            key: group.key,
            label: group.label,
            tables: group.entries,
        }));
    }, [dashboard]);

    const categoryOptions = useMemo<CategoryOption[]>(() => {
        const categories = dashboard?.menu ?? [];
        const mapped: CategoryOption[] = [];
        categories.forEach((category, index) => {
            const items = category.items ?? [];
            if (items.length === 0) {
                return;
            }
            const key = category.id != null
                ? `id-${category.id}`
                : category.name
                    ? `name-${category.name.toLowerCase()}`
                    : `index-${index}`;
            mapped.push({
                key,
                label: category.name ?? `Categoria ${index + 1}`,
                description: category.description ?? null,
                items,
                theme: resolveCategoryTheme(category.name, index),
                raw: category,
            });
        });
        return mapped;
    }, [dashboard]);

    useEffect(() => {
        if (categoryOptions.length === 0) {
            setSelectedCategoryKey(null);
            setCatalogPanelOpen(false);
            return;
        }
        setSelectedCategoryKey(prev => {
            if (prev && categoryOptions.some(option => option.key === prev)) {
                return prev;
            }
            return catalogPanelOpen ? (categoryOptions[0]?.key ?? null) : null;
        });
    }, [categoryOptions, catalogPanelOpen]);

    const normalizedSearchTerm = productSearch.trim().toLowerCase();
    const isSearching = normalizedSearchTerm.length > 0;

    const activeCategory = useMemo(() => {
        if (!catalogPanelOpen || isSearching || !selectedCategoryKey) {
            return null;
        }
        return categoryOptions.find(option => option.key === selectedCategoryKey) ?? null;
    }, [catalogPanelOpen, isSearching, selectedCategoryKey, categoryOptions]);

    const allCatalogItems = useMemo<CatalogItem[]>(() => {
        return categoryOptions.flatMap(option => option.items.map(item => ({ ...item, __category: option })));
    }, [categoryOptions]);

    const menuItemVariantsById = useMemo(() => {
        const map = new Map<number, PosMenuVariant[]>();
        allCatalogItems.forEach(item => {
            if (item.id == null) {
                return;
            }
            const variants = Array.isArray(item.variants)
                ? (item.variants.filter(variant => Boolean(variant) && variant.available !== false) as PosMenuVariant[])
                : [];
            if (variants.length > 0) {
                const sorted = [...variants].sort((a, b) => {
                    const positionA = a.position ?? Number.MAX_SAFE_INTEGER;
                    const positionB = b.position ?? Number.MAX_SAFE_INTEGER;
                    if (positionA !== positionB) {
                        return positionA - positionB;
                    }
                    const idA = a.id ?? Number.MAX_SAFE_INTEGER;
                    const idB = b.id ?? Number.MAX_SAFE_INTEGER;
                    return idA - idB;
                });
                map.set(item.id, sorted);
            }
        });
        return map;
    }, [allCatalogItems]);

    const filteredCatalogItems = useMemo<CatalogItem[]>(() => {
        if (!isSearching) {
            return [];
        }
        return allCatalogItems.filter(item => {
            const nameMatch = item.name?.toLowerCase().includes(normalizedSearchTerm);
            const descriptionMatch = item.description?.toLowerCase().includes(normalizedSearchTerm);
            return Boolean(nameMatch || descriptionMatch);
        });
    }, [allCatalogItems, isSearching, normalizedSearchTerm]);

    const displayedCatalogItems = useMemo<CatalogItem[]>(() => {
        if (isSearching) {
            return filteredCatalogItems;
        }
        if (!catalogPanelOpen || !activeCategory) {
            return [];
        }
        return activeCategory.items.map(item => ({ ...item, __category: activeCategory }));
    }, [isSearching, filteredCatalogItems, catalogPanelOpen, activeCategory]);

    const menuHasItems = categoryOptions.length > 0;

    // ============ METRICHE PER TAB BUTTONS ============
    const tablesMetrics = useMemo(() => {
        const allTables = dashboard?.tables ?? [];
        const occupied = allTables.filter(t => t.occupied).length;
        const needsPayment = allTables.filter(t => t.occupied && t.currentOrder?.status === 'open').length;
        const total = allTables.length;

        return [
            { label: 'Occupati', value: `${occupied}/${total}`, color: occupied > 0 ? 'red' as const : 'green' as const },
            { label: 'Da pagare', value: needsPayment, color: needsPayment > 0 ? 'orange' as const : undefined },
        ];
    }, [dashboard]);

    const ordersMetrics = useMemo(() => {
        const openOrders = dashboard?.activeOrders?.filter(o => o.status === 'open') ?? [];

        return [
            { label: 'In corso', value: openOrders.length, color: openOrders.length > 0 ? 'blue' as const : undefined },
        ];
    }, [dashboard]);
    // ==================================================

    const parsedPaymentInput = useMemo(() => {
        if (!paymentInput) {
            return 0;
        }
        const normalized = paymentInput.replace(',', '.');
        const numeric = Number(normalized);
        return Number.isFinite(numeric) && numeric >= 0 ? Number(numeric.toFixed(2)) : 0;
    }, [paymentInput]);

    const baseOrderItems = selectedOrder?.items ?? [];

    const resolveOrderItemKey = useCallback((item: PosOrderDetail['items'][number], index: number) => {
        if (item.id) {
            return `order-${item.id}`;
        }
        if (item.name) {
            return `order-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`;
        }
        return `order-${index}`;
    }, []);

    const resolveOrderUnitPrice = useCallback((item: PosOrderDetail['items'][number], originalQuantity: number) => {
        const explicitPrice = toNumber(item.price);
        if (explicitPrice > 0) {
            return roundCurrency(explicitPrice);
        }
        const totalValue = toNumber(item.total);
        if (originalQuantity > 0) {
            return roundCurrency(totalValue / originalQuantity);
        }
        return roundCurrency(totalValue);
    }, []);

    const orderComputation = useMemo(() => {
        if (baseOrderItems.length === 0) {
            return {
                items: [] as Array<{
                    key: string;
                    item: PosOrderDetail['items'][number];
                    name: string;
                    modifiers: string[];
                    comment?: string;
                    originalQuantity: number;
                    quantity: number;
                    unitPrice: number;
                    originalTotal: number;
                    adjustedTotal: number;
                    quantityChanged: boolean;
                    removed: boolean;
                }>,
                originalTotal: 0,
                adjustedTotal: 0,
            };
        }
        let originalTotal = 0;
        let adjustedTotal = 0;
        const items = baseOrderItems.map((item, index) => {
            const key = resolveOrderItemKey(item, index);
            const originalQuantityRaw = item.quantity ?? 1;
            const originalQuantity = originalQuantityRaw && originalQuantityRaw > 0 ? originalQuantityRaw : 1;
            const quantity = clampQuantity(orderAdjustments[key] ?? originalQuantity);
            const unitPrice = resolveOrderUnitPrice(item, originalQuantity);
            const originalItemTotal = roundCurrency(unitPrice * originalQuantity);
            const adjustedItemTotal = roundCurrency(unitPrice * quantity);
            const rawComment = typeof item.comment === 'string' ? item.comment.trim() : '';
            const comment = rawComment.length > 0 ? rawComment : undefined;
            originalTotal += originalItemTotal;
            adjustedTotal += adjustedItemTotal;
            return {
                key,
                item,
                name: item.name ?? 'Articolo',
                modifiers: item.modifiers ?? [],
                comment,
                originalQuantity,
                quantity,
                unitPrice,
                originalTotal: originalItemTotal,
                adjustedTotal: adjustedItemTotal,
                quantityChanged: quantity !== originalQuantity,
                removed: quantity === 0,
            };
        });
        return {
            items,
            originalTotal: roundCurrency(originalTotal),
            adjustedTotal: roundCurrency(adjustedTotal),
        };
    }, [baseOrderItems, orderAdjustments, resolveOrderItemKey, resolveOrderUnitPrice]);

    const orderItemsForDisplay = orderComputation.items;
    const hasOrderItems = orderItemsForDisplay.length > 0;
    const hasDraftItems = draftItems.length > 0;
    const buildOrderUpdatePayload = useCallback(() => {
        const adjustments = orderItemsForDisplay
            .filter(detail => detail.quantity !== detail.originalQuantity)
            .map(detail => ({
                reference: detail.item.id ?? detail.item.name ?? detail.key,
                name: detail.name,
                quantity: detail.quantity,
            }));
        const additions = draftItems.map(draft => {
            const quantity = Math.max(1, clampQuantity(draft.quantity));
            const price = roundCurrency(draft.price);
            const reference = typeof draft.sourceId === 'number'
                ? `menu-${draft.sourceId}`
                : draft.key;
            return {
                reference,
                name: draft.name || 'Articolo',
                menuItemId: typeof draft.sourceId === 'number' ? draft.sourceId : undefined,
                price,
                quantity,
                custom: Boolean(draft.isCustom || draft.sourceId == null),
            };
        });
        return { adjustments, additions };
    }, [orderItemsForDisplay, draftItems]);

    const paymentSummary = useMemo(() => {
        const originalBaseTotal = selectedOrder ? orderComputation.originalTotal : 0;
        const baseTotal = selectedOrder ? orderComputation.adjustedTotal : 0;
        const orderDelta = selectedOrder ? roundCurrency(baseTotal - originalBaseTotal) : 0;
        const additionalTotal = draftItems.reduce((sum, item) => {
            const quantity = Number.isFinite(item.quantity) && item.quantity ? item.quantity : 1;
            const unitTotal = roundCurrency(toNumber(item.price) * quantity);
            return sum + unitTotal;
        }, 0);
        const grossTotal = roundCurrency(baseTotal + additionalTotal);

        let discountAmount = 0;
        if (discount) {
            if (discount.type === 'amount') {
                discountAmount = discount.value;
            } else {
                discountAmount = grossTotal * (discount.value / 100);
            }
        }
        if (!Number.isFinite(discountAmount) || discountAmount < 0) {
            discountAmount = 0;
        }
        discountAmount = Math.min(grossTotal, roundCurrency(discountAmount));

        let voucherAmount = 0;
        if (mealVoucher) {
            voucherAmount = mealVoucher.count * mealVoucher.value;
        }
        if (!Number.isFinite(voucherAmount) || voucherAmount < 0) {
            voucherAmount = 0;
        }
        voucherAmount = Math.min(Math.max(grossTotal - discountAmount, 0), roundCurrency(voucherAmount));

        const netTotal = Math.max(0, roundCurrency(grossTotal - discountAmount - voucherAmount));
        const paymentAmount = Math.max(0, roundCurrency(parsedPaymentInput));
        const changeDue = Math.max(0, roundCurrency(paymentAmount - netTotal));
        const outstanding = Math.max(0, roundCurrency(netTotal - paymentAmount));

        const shares = splitInfo?.shares && splitInfo.shares > 1 ? splitInfo.shares : null;
        const perShare = shares ? netTotal / shares : null;

        return {
            baseTotal,
            additionalTotal,
            grossTotal,
            netTotal,
            discountAmount,
            voucherAmount,
            paymentAmount,
            changeDue,
            outstanding,
            shares,
            perShare,
            originalBaseTotal,
            orderDelta,
            totalFormatted: formatCurrency(netTotal),
            inputFormatted: formatCurrency(paymentAmount),
            remainingFormatted: formatCurrency(changeDue),
            outstandingFormatted: formatCurrency(outstanding),
            discountFormatted: formatCurrency(discountAmount),
            voucherFormatted: formatCurrency(voucherAmount),
            baseFormatted: formatCurrency(baseTotal),
            originalBaseFormatted: formatCurrency(originalBaseTotal),
            orderDeltaFormatted: formatCurrency(Math.abs(orderDelta)),
            additionalFormatted: formatCurrency(additionalTotal),
            perShareFormatted: perShare != null ? formatCurrency(perShare) : null,
        };
    }, [selectedOrder, orderComputation, draftItems, discount, mealVoucher, parsedPaymentInput, splitInfo]);

    const linkedCompanyName = useMemo(() => {
        return extractSnapshotString(invoiceInfo?.fiscalSnapshot, [
            'companyName',
            'businessName',
            'denomination',
            'ragioneSociale',
            'name',
        ]);
    }, [invoiceInfo?.fiscalSnapshot]);

    const linkedCompanyVat = useMemo(() => {
        return extractSnapshotString(invoiceInfo?.fiscalSnapshot, [
            'vatNumber',
            'vat',
            'partitaIva',
            'piva',
            'pIva',
            'vatnumber',
        ]);
    }, [invoiceInfo?.fiscalSnapshot]);

    const companySelectOptions = useMemo(() => {
        const options = companyResults.map(entry => {
            const vatLabel = entry.vatNumber ? ` — P.IVA ${entry.vatNumber}` : '';
            return {
                id: entry.id,
                label: `${entry.companyName}${vatLabel}`,
            };
        });
        if (invoiceInfo?.companyId && !options.some(option => option.id === invoiceInfo.companyId)) {
            const fallbackLabelParts: string[] = [];
            if (linkedCompanyName) {
                fallbackLabelParts.push(linkedCompanyName);
            }
            fallbackLabelParts.push(`ID ${invoiceInfo.companyId}`);
            const fallbackLabel = fallbackLabelParts.join(' · ');
            options.unshift({ id: invoiceInfo.companyId, label: fallbackLabel });
        }
        return options;
    }, [companyResults, invoiceInfo, linkedCompanyName]);

    const invoiceIssued = useMemo(() => {
        if (!invoiceInfo) {
            return false;
        }
        return Boolean(invoiceInfo.invoiceNumber || invoiceInfo.emittedAt || invoiceInfo.sentAt);
    }, [invoiceInfo]);

    const hasLinkedCompany = Boolean(invoiceInfo?.companyId);

    const invoiceStatusMeta = useMemo(() => {
        type StatusVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';
        if (!selectedOrder) {
            return {
                variant: 'neutral' as StatusVariant,
                label: 'Fatturazione non disponibile',
                description: 'Seleziona un ordine aperto per gestire la fattura.',
            };
        }
        if (invoiceLoading) {
            return {
                variant: 'info' as StatusVariant,
                label: 'Caricamento fatturazione…',
                description: 'Recupero dei dati fiscali in corso.',
            };
        }
        if (!invoiceInfo) {
            return {
                variant: 'neutral' as StatusVariant,
                label: 'Nessuna anagrafica collegata',
                description: 'Collega un\'azienda per generare la fattura.',
            };
        }
        if (invoiceIssued) {
            const reference = invoiceInfo.invoiceNumber ?? invoiceInfo.documentReference;
            const when = invoiceInfo.invoiceDate ?? invoiceInfo.emittedAt ?? invoiceInfo.sentAt;
            const formattedDate = when ? new Date(when).toLocaleString('it-IT') : null;
            return {
                variant: 'success' as StatusVariant,
                label: reference ? `Fattura ${reference}` : 'Fattura emessa',
                description: formattedDate ? `Emessa il ${formattedDate}` : 'Fattura inviata con successo.',
            };
        }
        const status = (invoiceInfo.invoiceStatus ?? '').toLowerCase();
        if (status.includes('error') || status.includes('fail') || status.includes('reject')) {
            return {
                variant: 'error' as StatusVariant,
                label: 'Invio non riuscito',
                description: invoiceInfo.providerMessage?.toString() || 'Verifica i dati fiscali e riprova.',
            };
        }
        if (status.includes('pending') || status.includes('process') || status.includes('sending')) {
            return {
                variant: 'warning' as StatusVariant,
                label: 'Invio in elaborazione',
                description: 'Attendiamo l\'esito dal sistema SDI.',
            };
        }
        if (hasLinkedCompany) {
            return {
                variant: 'info' as StatusVariant,
                label: 'Anagrafica collegata',
                description: 'Pronto per l\'invio della fattura.',
            };
        }
        return {
            variant: 'neutral' as StatusVariant,
            label: 'Nessuna anagrafica collegata',
            description: 'Collega un\'azienda per generare la fattura.',
        };
    }, [selectedOrder, invoiceLoading, invoiceInfo, invoiceIssued, hasLinkedCompany]);

    const linkDisabled = !selectedOrderId || !selectedCompanyId || invoiceSubmitting || invoiceLoading || selectedCompanyId === invoiceInfo?.companyId;
    const issueDisabled = !selectedOrderId || !hasLinkedCompany || invoiceSubmitting || invoiceLoading || invoiceIssued;
    const showInvoicePanel = Boolean(selectedOrder) && invoicePanelOpen;

    const openAdjustmentModal = useCallback((type: AdjustmentModalType) => {
        const next = buildDefaultAdjustmentForm();
        if (type === 'discount' && discount) {
            next.discountMode = discount.type;
            next.discountValue = String(discount.value);
        }
        if (type === 'voucher' && mealVoucher) {
            next.voucherCount = String(mealVoucher.count);
            next.voucherValue = mealVoucher.value.toFixed(2);
        }
        if (type === 'split' && splitInfo?.shares) {
            next.splitShares = String(splitInfo.shares);
        }
        if (type === 'customTotal') {
            next.customTotal = paymentSummary.netTotal.toFixed(2);
        }
        setAdjustmentForm(next);
        setAdjustmentError(null);
        setAdjustmentModal(type);
    }, [discount, mealVoucher, splitInfo, paymentSummary]);

    const closeAdjustmentModal = useCallback(() => {
        setAdjustmentModal(null);
        setAdjustmentError(null);
        setAdjustmentForm(buildDefaultAdjustmentForm());
    }, []);

    const handleAdjustmentSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!adjustmentModal) {
            return;
        }
        setAdjustmentError(null);
        switch (adjustmentModal) {
            case 'customTotal': {
                const normalized = adjustmentForm.customTotal.replace(',', '.').trim();
                if (normalized.length === 0) {
                    setDiscount(null);
                    closeAdjustmentModal();
                    return;
                }
                const parsed = Number.parseFloat(normalized);
                if (!Number.isFinite(parsed) || parsed < 0) {
                    setAdjustmentError('Inserisci un importo valido.');
                    return;
                }
                const desired = roundCurrency(parsed);
                const gross = roundCurrency(paymentSummary.grossTotal);
                if (desired >= gross) {
                    setDiscount(null);
                } else {
                    const diff = roundCurrency(gross - desired);
                    if (diff <= 0) {
                        setDiscount(null);
                    } else {
                        setDiscount({ type: 'amount', value: diff });
                    }
                }
                closeAdjustmentModal();
                break;
            }
            case 'discount': {
                const normalized = adjustmentForm.discountValue.replace(',', '.').trim();
                if (normalized.length === 0) {
                    setDiscount(null);
                    closeAdjustmentModal();
                    return;
                }
                const parsed = Number.parseFloat(normalized);
                if (!Number.isFinite(parsed) || parsed < 0) {
                    setAdjustmentError('Valore sconto non valido.');
                    return;
                }
                if (adjustmentForm.discountMode === 'percentage') {
                    const bounded = Math.min(100, Math.max(0, Number(parsed.toFixed(2))));
                    if (bounded <= 0) {
                        setDiscount(null);
                    } else {
                        setDiscount({ type: 'percentage', value: bounded });
                    }
                } else {
                    const gross = roundCurrency(paymentSummary.grossTotal);
                    const bounded = Math.min(gross, Math.max(0, roundCurrency(parsed)));
                    if (bounded <= 0) {
                        setDiscount(null);
                    } else {
                        setDiscount({ type: 'amount', value: bounded });
                    }
                }
                closeAdjustmentModal();
                break;
            }
            case 'voucher': {
                const countRaw = adjustmentForm.voucherCount.trim();
                const valueRaw = adjustmentForm.voucherValue.replace(',', '.').trim();
                if (!countRaw && !valueRaw) {
                    setMealVoucher(null);
                    closeAdjustmentModal();
                    return;
                }
                const count = Number.parseInt(countRaw, 10);
                const value = Number.parseFloat(valueRaw);
                if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(value) || value <= 0) {
                    setAdjustmentError('Inserisci numero e valore dei buoni validi.');
                    return;
                }
                setMealVoucher({ count, value: roundCurrency(value) });
                closeAdjustmentModal();
                break;
            }
            case 'split': {
                const normalized = adjustmentForm.splitShares.trim();
                if (normalized.length === 0) {
                    setSplitInfo(null);
                    closeAdjustmentModal();
                    return;
                }
                const shares = Number.parseInt(normalized, 10);
                if (!Number.isFinite(shares) || shares < 2) {
                    setAdjustmentError('Inserisci un numero di persone maggiore o uguale a 2.');
                    return;
                }
                setSplitInfo({ shares });
                closeAdjustmentModal();
                break;
            }
            default:
                break;
        }
    }, [adjustmentModal, adjustmentForm, paymentSummary, closeAdjustmentModal, setDiscount, setMealVoucher, setSplitInfo]);

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => !prev);
    }, []);

    const canAdjustPayments = Boolean((selectedOrder || hasDraftItems) && !orderSubmitting);
    const discountLabel = discount
        ? discount.type === 'amount'
            ? `Sconto (${formatCurrency(discount.value)})`
            : `Sconto (${roundCurrency(discount.value)}%)`
        : 'Sconto';
    const voucherLabel = mealVoucher
        ? `Buoni pasto (${mealVoucher.count} x ${formatCurrency(mealVoucher.value)})`
        : 'Buoni pasto';
    const splitLabel = splitInfo?.shares
        ? `Dividi conto (${splitInfo.shares})`
        : 'Dividi conto';
    const customTotalLabel = 'Prezzo personalizzato';
    const currentAdjustmentMeta = adjustmentModal ? ADJUSTMENT_TAB_META[adjustmentModal] : null;
    const splitSharesPreviewValue = Number.parseInt(adjustmentForm.splitShares, 10);
    const splitPreviewAmount = Number.isFinite(splitSharesPreviewValue) && splitSharesPreviewValue > 1
        ? formatCurrency(roundCurrency(paymentSummary.netTotal / splitSharesPreviewValue))
        : null;
    const sidebarToggleLabel = sidebarCollapsed ? 'Mostra tavoli e ordini' : 'Riduci sidebar';
    const SidebarToggleIcon = sidebarCollapsed ? FiMaximize2 : FiSidebar;
    const voucherCountPreview = Number.parseInt(adjustmentForm.voucherCount, 10);
    const voucherValuePreview = Number.parseFloat(adjustmentForm.voucherValue.replace(',', '.'));
    const voucherPreviewAmount = Number.isFinite(voucherCountPreview) && voucherCountPreview > 0 && Number.isFinite(voucherValuePreview) && voucherValuePreview > 0
        ? formatCurrency(roundCurrency(voucherCountPreview * voucherValuePreview))
        : null;

    useEffect(() => {
        if (!canAdjustPayments && adjustmentModal) {
            closeAdjustmentModal();
        }
    }, [canAdjustPayments, adjustmentModal, closeAdjustmentModal]);

    const isCounterOrder = !selectedOrder && hasDraftItems && !selectedTableNumber;
    const isNewTableOrder = !selectedOrder && hasDraftItems && selectedTableNumber;
    const orderStatusLabel = selectedOrder
        ? (selectedOrder.status ?? 'APERTO')
        : isCounterOrder
            ? 'ORDINE AL BANCO'
            : isNewTableOrder
                ? 'NUOVO ORDINE'
                : null;
    const orderTitleLabel = selectedOrder
        ? selectedOrder.code ?? `Ordine #${selectedOrder.id}`
        : isCounterOrder
            ? 'Ordine al banco'
            : isNewTableOrder
                ? `Nuovo ordine - Tavolo ${selectedTableNumber}`
                : '';
    const orderSecondaryLabel = selectedOrder
        ? `Tavolo ${selectedOrder.tableNumber ?? 'N/D'}`
        : isCounterOrder
            ? 'Banco'
            : isNewTableOrder
                ? `Tavolo ${selectedTableNumber}`
                : '';

    const resolveMenuDraftPrice = useCallback((item: { price?: number | null; counterPrice?: number | null }) => {
        const tablePrice = roundCurrency(toNumber(item.price));
        const counterItemPrice = roundCurrency(toNumber(item.counterPrice));

        // Usa priceMode per determinare quale prezzo applicare
        if (priceMode === 'counter') {
            // Modalità banco: usa counterPrice se disponibile, altrimenti price
            if (counterItemPrice > 0) {
                return counterItemPrice;
            }
        }

        // Modalità tavolo o fallback: usa sempre price
        if (tablePrice > 0) {
            return tablePrice;
        }
        return counterItemPrice > 0 ? counterItemPrice : 0;
    }, [priceMode]);

    const showItemAddedBanner = useCallback((label: string) => {
        const normalized = label.trim().length > 0 ? label.trim() : 'Articolo';
        setBanner({ type: 'success', message: `${normalized} aggiunto al conto.` });
        window.setTimeout(() => setBanner(null), 1800);
    }, [setBanner]);

    const addMenuItemToDraft = useCallback((item: CatalogItem): string => {
        const sourceId = item.id ?? null;
        const basePrice = resolveMenuDraftPrice(item);
        const normalizedName = (item.name ?? 'Articolo').trim() || 'Articolo';
        const sanitized = normalizedName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const baseKey = sourceId !== null
            ? `menu-${sourceId}`
            : `menu-${item.__category.key}-${sanitized || 'item'}`;
        const derivedKey = `${baseKey}-${Date.now()}`;

        setDraftItems(prev => {
            const next = [...prev];
            const existingIndex = next.findIndex(draft => {
                if (draft.isCustom || draft.isVariant) {
                    return false;
                }
                if ((draft.sourceId ?? null) !== sourceId) {
                    return false;
                }
                if (sourceId == null) {
                    return draft.name.toLowerCase() === normalizedName.toLowerCase()
                        && Math.abs(draft.price - basePrice) < 0.01;
                }
                return true;
            });

            if (existingIndex >= 0) {
                const existing = next[existingIndex];
                next[existingIndex] = {
                    ...existing,
                    quantity: Math.min(existing.quantity + 1, 99),
                    price: basePrice,
                    basePrice,
                    baseName: existing.baseName ?? normalizedName,
                };
                return next;
            }

            next.push({
                key: derivedKey,
                sourceId,
                name: normalizedName,
                baseName: normalizedName,
                price: basePrice,
                basePrice,
                quantity: 1,
            });
            return next;
        });

        return normalizedName;
    }, [resolveMenuDraftPrice]);

    const addVariantToDraft = useCallback((parentKey: string, parent: DraftItem, variant: PosMenuVariant): string | null => {
        const variantKey = buildVariantKey(variant);
        const parentName = parent.baseName ?? parent.name ?? 'Articolo';
        const variantName = (variant.name ?? '').trim() || 'Variante';
        const variantPrice = roundCurrency(toNumber(variant.price));
        const effectivePrice = variantPrice > 0 ? variantPrice : roundCurrency(parent.basePrice ?? parent.price);
        const displayName = `${parentName} · ${variantName}`;

        setDraftItems(prev => {
            const parentIndex = prev.findIndex(item => item.key === parentKey);
            if (parentIndex === -1) {
                return prev;
            }

            const next = [...prev];
            const existingIndex = next.findIndex(item =>
                item.isVariant
                && item.parentKey === parentKey
                && (item.variantKey ?? '') === variantKey);

            if (existingIndex >= 0) {
                const existing = next[existingIndex];
                next[existingIndex] = {
                    ...existing,
                    quantity: Math.min(existing.quantity + 1, 99),
                    price: effectivePrice,
                };
                return next;
            }

            const variantItem: DraftItem = {
                key: `${parentKey}|variant-${variantKey}-${Date.now()}`,
                sourceId: parent.sourceId,
                name: displayName,
                baseName: parentName,
                price: effectivePrice,
                basePrice: effectivePrice,
                quantity: 1,
                isVariant: true,
                parentKey,
                variantId: variant.id ?? null,
                variantKey,
                variantName,
            };
            next.splice(parentIndex + 1, 0, variantItem);
            return next;
        });

        return displayName;
    }, []);

    const handleSelectOrder = useCallback((order: PosOrderPreview | null) => {
        setSelectedOrderId(order?.id ?? null);
    }, []);

    const handleSelectTable = useCallback((table: PosTable) => {
        if (table.currentOrder && typeof table.currentOrder.id === 'number') {
            // Tavolo occupato - carica ordine esistente e mostra dettagli
            setSelectedOrderId(table.currentOrder.id);
            setSelectedTableNumber(null);
            setDraftItems([]);
            // 🔄 Passa automaticamente alla tab "Ordini Aperti"
            setActiveTab('orders');
        } else {
            // Tavolo libero - permetti creazione nuovo ordine
            setSelectedOrderId(null);
            setSelectedTableNumber(table.tableNumber ?? null);
            setDraftItems([]);
            setPaymentInput('');
            setDiscount(null);
            setMealVoucher(null);
            setSplitInfo(null);
        }
    }, []);

    const handleDeleteTable = useCallback(async (tableNumber: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Previeni il click sulla card

        if (!confirm(`Sei sicuro di voler eliminare il tavolo ${tableNumber}?`)) {
            return;
        }

        try {
            await deletePosTable(tableNumber);
            setBanner({ type: 'success', message: `Tavolo ${tableNumber} eliminato con successo` });
            window.setTimeout(() => setBanner(null), 2500);

            // Ricarica dashboard
            await loadDashboard();

            // Se il tavolo eliminato era selezionato, deseleziona
            if (selectedTableNumber === tableNumber) {
                setSelectedTableNumber(null);
                setDraftItems([]);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile eliminare il tavolo';
            setBanner({ type: 'error', message });
            window.setTimeout(() => setBanner(null), 3000);
        }
    }, [selectedTableNumber, loadDashboard, setBanner]);

    const handleSelectCategory = useCallback((categoryKey: string) => {
        setProductSearch('');
        setSelectedCategoryKey(categoryKey);
        setCatalogPanelOpen(true);
    }, []);

    const handleCloseCatalogPanel = useCallback(() => {
        setCatalogPanelOpen(false);
        setSelectedCategoryKey(null);
        setProductSearch('');
    }, []);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductSearch(value);
        const trimmed = value.trim();
        if (trimmed.length > 0) {
            setCatalogPanelOpen(true);
            setSelectedCategoryKey(null);
        } else if (!selectedCategoryKey) {
            setCatalogPanelOpen(false);
        }
    }, [selectedCategoryKey]);

    const handleClearSearch = useCallback(() => {
        const hadCategory = Boolean(selectedCategoryKey);
        setProductSearch('');
        if (!hadCategory) {
            setCatalogPanelOpen(false);
        } else {
            setSelectedCategoryKey(categoryOptions[0]?.key ?? null);
        }
    }, [selectedCategoryKey, categoryOptions]);

    const handleSearchFocus = useCallback(() => {
        if (productSearch.trim().length > 0 || selectedCategoryKey) {
            setCatalogPanelOpen(true);
        }
    }, [productSearch, selectedCategoryKey]);

    const openCustomItemModal = useCallback(() => {
        setCustomItemModalOpen(true);
        setCustomItemForm({ name: '', price: '', quantity: '1' });
        setCustomItemError(null);
    }, [setCustomItemError, setCustomItemForm, setCustomItemModalOpen]);

    const closeCustomItemModal = useCallback(() => {
        setCustomItemForm({ name: '', price: '', quantity: '1' });
        setCustomItemModalOpen(false);
        setCustomItemError(null);
    }, [setCustomItemError, setCustomItemForm, setCustomItemModalOpen]);

    // 🍽️ Gestione modale coperto (manuale)
    const openCoverChargeModal = useCallback(() => {
        if (coverChargeAmount > 0) {
            setNumberOfPeople('2');
            setCoverChargeModalOpen(true);
        }
    }, [coverChargeAmount]);

    const handleCoverChargeConfirm = useCallback(() => {
        const people = parseInt(numberOfPeople, 10);
        if (people > 0 && coverChargeAmount > 0) {
            const totalCoverCharge = roundCurrency(people * coverChargeAmount);
            const coverItem: DraftItem = {
                key: `cover-${Date.now()}`,
                name: `Coperto (${people} ${people === 1 ? 'persona' : 'persone'})`,
                price: totalCoverCharge,
                quantity: 1,
                isCustom: true,
            };
            setDraftItems(prev => [coverItem, ...prev]);
        }
        setCoverChargeModalOpen(false);
        setNumberOfPeople('2');
    }, [numberOfPeople, coverChargeAmount]);

    const handleCoverChargeSkip = useCallback(() => {
        setCoverChargeModalOpen(false);
        setNumberOfPeople('2');
    }, []);

    const handleCustomItemSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedName = customItemForm.name.trim();
        const sanitizedPriceInput = customItemForm.price.replace(/[\s€]/g, '').replace(',', '.');
        const parsedPrice = Number.parseFloat(sanitizedPriceInput);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
            setCustomItemError('Inserisci un prezzo valido maggiore di zero.');
            return;
        }
        const priceValue = roundCurrency(parsedPrice);
        if (priceValue <= 0) {
            setCustomItemError('Il prezzo deve essere superiore a zero.');
            return;
        }
        const sanitizedQuantityInput = customItemForm.quantity.replace(/[^0-9]/g, '');
        const parsedQuantity = Number.parseInt(sanitizedQuantityInput, 10);
        let quantityValue = clampQuantity(Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1);
        if (quantityValue <= 0) {
            quantityValue = 1;
        }
        const normalizedName = trimmedName.length > 0 ? trimmedName : 'Articolo personalizzato';

        setDraftItems(prev => {
            const next = [...prev];
            const existingIndex = next.findIndex(item =>
                item.isCustom
                && item.name.toLowerCase() === normalizedName.toLowerCase()
                && Math.abs(item.price - priceValue) < 0.01,
            );
            if (existingIndex >= 0) {
                const existing = next[existingIndex];
                next[existingIndex] = {
                    ...existing,
                    quantity: Math.min(existing.quantity + quantityValue, 99),
                };
                return next;
            }
            next.push({
                key: `custom-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                name: normalizedName,
                price: priceValue,
                quantity: quantityValue,
                isCustom: true,
            });
            return next;
        });

        setCustomItemModalOpen(false);
        setCustomItemError(null);
        showItemAddedBanner(normalizedName);
    }, [customItemForm, showItemAddedBanner, setDraftItems, setCustomItemForm]);

    const handleSelectCatalogItem = useCallback((item: CatalogItem) => {
        const label = addMenuItemToDraft(item);
        showItemAddedBanner(label);
    }, [addMenuItemToDraft, showItemAddedBanner]);

    const handleIncrementDraftItem = useCallback((draftKey: string) => {
        setDraftItems(prev => prev.map(item => (item.key === draftKey ? { ...item, quantity: Math.min(item.quantity + 1, 99) } : item)));
    }, []);

    const handleDecrementDraftItem = useCallback((draftKey: string) => {
        setDraftItems(prev => {
            const target = prev.find(item => item.key === draftKey);
            if (!target) {
                return prev;
            }
            if (target.quantity <= 1) {
                if (target.isVariant) {
                    return prev.filter(item => item.key !== draftKey);
                }
                return prev.filter(item => item.key !== draftKey && item.parentKey !== draftKey);
            }
            return prev.map(item => item.key === draftKey ? { ...item, quantity: item.quantity - 1 } : item);
        });
    }, []);

    const handleRemoveDraftItem = useCallback((draftKey: string) => {
        setDraftItems(prev => {
            const target = prev.find(item => item.key === draftKey);
            if (!target) {
                return prev;
            }
            if (target.isVariant) {
                return prev.filter(item => item.key !== draftKey);
            }
            return prev.filter(item => item.key !== draftKey && item.parentKey !== draftKey);
        });
    }, []);

    const handleDraftVariantSelect = useCallback((draftKey: string, variantKey: string) => {
        if (!variantKey) {
            return;
        }
        const parent = draftItems.find(item => item.key === draftKey);
        if (!parent || parent.isVariant) {
            return;
        }
        const sourceId = typeof parent.sourceId === 'number' ? parent.sourceId : null;
        if (sourceId == null) {
            return;
        }
        const variants = menuItemVariantsById.get(sourceId);
        if (!variants || variants.length === 0) {
            return;
        }
        const selected = variants.find(option => buildVariantKey(option) === variantKey);
        if (!selected) {
            return;
        }
        const label = addVariantToDraft(draftKey, parent, selected);
        if (label) {
            showItemAddedBanner(label);
        }
        setVariantDraftSelections(prev => ({ ...prev, [draftKey]: '' }));
    }, [draftItems, menuItemVariantsById, addVariantToDraft, showItemAddedBanner]);

    const adjustOrderItemQuantity = useCallback((orderKey: string, originalQuantity: number, delta: number) => {
        setOrderAdjustments(prev => {
            const current = prev[orderKey] ?? originalQuantity;
            const next = clampQuantity(current + delta);
            if (next === originalQuantity) {
                if (prev[orderKey] === undefined) {
                    return prev;
                }
                const { [orderKey]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [orderKey]: next };
        });
    }, []);

    const handleRemoveOrderItem = useCallback((orderKey: string) => {
        setOrderAdjustments(prev => {
            if (prev[orderKey] === 0) {
                return prev;
            }
            return { ...prev, [orderKey]: 0 };
        });
    }, []);

    const keypadRows = useMemo(() => [
        ['7', '8', '9'],
        ['4', '5', '6'],
        ['1', '2', '3'],
        ['0', '00', '.'],
    ], []);

    const keypadUtilities = useMemo(() => ['⌫', 'C'], []);

    const handleKeypadPress = useCallback((key: string) => {
        setPaymentInput(prev => {
            if (key === 'C') {
                return '';
            }
            if (key === '⌫') {
                return prev.slice(0, -1);
            }
            if (key === '.') {
                if (prev.includes('.') || prev.includes(',')) {
                    return prev;
                }
                return prev.length === 0 ? '0.' : `${prev}.`;
            }
            const next = `${prev}${key}`;
            return next.length > 10 ? next.slice(0, 10) : next;
        });
    }, []);

    const handlePaymentAction = useCallback(async (action: 'cash' | 'pos' | 'ticket' | 'close') => {
        // Se non c'è ordine selezionato ma ci sono draft items (conto al banco)
        // creiamo prima l'ordine senza tavolo
        if (!selectedOrder && hasDraftItems && !orderSubmitting) {
            // Crea ordine al banco prima di procedere con il pagamento
            try {
                setOrderSubmitting(true);
                const items = draftItems.map(draft => ({
                    reference: typeof draft.sourceId === 'number' ? `menu-${draft.sourceId}` : draft.key,
                    name: draft.name || 'Articolo',
                    menuItemId: typeof draft.sourceId === 'number' ? draft.sourceId : undefined,
                    price: roundCurrency(draft.price),
                    quantity: Math.max(1, clampQuantity(draft.quantity)),
                    custom: Boolean(draft.isCustom || draft.sourceId == null),
                }));

                const payload: CreatePosOrderPayload = {
                    tableNumber: 'BANCO', // Conto al banco senza tavolo
                    customerName: 'Banco',
                    items,
                };

                const newOrder = await createPosOrder(payload);
                setSelectedOrderId(newOrder.id);
                setDraftItems([]);

                await loadDashboard();

                // Ora procedi con il pagamento (solo per cash/pos)
                if (action === 'cash' || action === 'pos') {
                    setPendingPaymentAction(action);
                    setPaymentModalOpen(true);
                }
                setOrderSubmitting(false);
                return;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Impossibile creare il conto al banco.';
                setBanner({ type: 'error', message });
                setOrderSubmitting(false);
                return;
            }
        }

        if (!selectedOrder) {
            return;
        }
        if (orderSubmitting) {
            return;
        }

        // Per pagamenti cash/pos, apri il modal
        if (action === 'cash' || action === 'pos') {
            setPendingPaymentAction(action);
            setPaymentModalOpen(true);
            return;
        }

        // Per ticket e close, procedi come prima
        const actionLabels: Record<'ticket' | 'close', string> = {
            ticket: 'Emissione scontrino',
            close: 'Chiusura ordine',
        };
        const method: 'CUSTOM' = 'CUSTOM';
        const targetLabel = selectedOrder.code ?? `ordine #${selectedOrder.id}`;
        const payload = buildOrderUpdatePayload();
        const amountDue = paymentSummary.netTotal;
        const amountReceived = paymentSummary.paymentAmount > 0 ? paymentSummary.paymentAmount : amountDue;
        const changeAmount = Math.max(0, roundCurrency(amountReceived - amountDue));
        const outstandingAmount = Math.max(0, roundCurrency(amountDue - amountReceived));
        if (action !== 'close' && outstandingAmount > 0) {
            setBanner({ type: 'error', message: 'Inserisci l\'importo incassato prima di registrare il pagamento.' });
            window.setTimeout(() => setBanner(null), 2800);
            return;
        }
        const paymentPayload: PosOrderPaymentPayload = {
            ...payload,
            method,
            amountDue,
            amountReceived,
            discountAmount: paymentSummary.discountAmount,
            voucherAmount: paymentSummary.voucherAmount,
            changeAmount,
            outstandingAmount,
            note: action === 'ticket' ? 'Emissione scontrino POS' : action === 'close' ? 'Chiusura manuale POS' : undefined,
        };
        setOrderSubmitting(true);
        try {
            await registerPosOrderPayment(selectedOrder.id, paymentPayload);
            setBanner({ type: 'success', message: `${actionLabels[action]} registrata per ${targetLabel}.` });
            window.setTimeout(() => setBanner(null), 2500);
            setOrderAdjustments({});
            setDraftItems([]);
            setPaymentInput('');
            setDiscount(null);
            setMealVoucher(null);
            setSplitInfo(null);
            setAdjustmentModal(null);
            setAdjustmentForm(buildDefaultAdjustmentForm());
            await Promise.all([loadDashboard(), loadSummary()]);
            setSelectedOrderId(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile registrare il pagamento.';
            setBanner({ type: 'error', message });
        } finally {
            setOrderSubmitting(false);
        }
    }, [selectedOrder, hasDraftItems, orderSubmitting, buildOrderUpdatePayload, paymentSummary, loadDashboard, loadSummary, setBanner]);

    const handleConfirmPayment = useCallback(async (paidAmount: number, method: 'CASH' | 'POS' | 'CUSTOM') => {
        if (!selectedOrder || !pendingPaymentAction) {
            return;
        }

        const action = pendingPaymentAction;
        const getActionLabel = (act: 'cash' | 'pos'): string => {
            const labels = {
                cash: 'Pagamento contanti',
                pos: 'Pagamento POS/Carta',
            };
            return labels[act];
        };

        const targetLabel = selectedOrder.code ?? `ordine #${selectedOrder.id}`;
        const payload = buildOrderUpdatePayload();
        const amountDue = paymentSummary.netTotal;
        const amountReceived = paidAmount;
        const changeAmount = Math.max(0, roundCurrency(amountReceived - amountDue));
        const outstandingAmount = Math.max(0, roundCurrency(amountDue - amountReceived));

        const paymentPayload: PosOrderPaymentPayload = {
            ...payload,
            method,
            amountDue,
            amountReceived,
            discountAmount: paymentSummary.discountAmount,
            voucherAmount: paymentSummary.voucherAmount,
            changeAmount,
            outstandingAmount,
            note: undefined,
        };

        setOrderSubmitting(true);
        setPaymentModalOpen(false);
        setPendingPaymentAction(null);

        try {
            await registerPosOrderPayment(selectedOrder.id, paymentPayload);
            // 🎯 STAMPA AUTOMATICA SCONTRINO dopo pagamento
            console.log(`📄 Stampa automatica scontrino per ordine ${selectedOrder.code} - Pagamento ${method}`);
            // TODO: Integrare con stampante fiscale quando disponibile
            // await printReceipt(selectedOrder.id);

            showPaymentConfirmation({
                title: `${getActionLabel(action)} completato`,
                orderLabel: targetLabel,
                subtitle: 'Scontrino inviato alla stampante.',
                changeLabel: changeAmount > 0 ? formatCurrency(changeAmount) : undefined,
            });
            setOrderAdjustments({});
            setDraftItems([]);
            setPaymentInput('');
            setDiscount(null);
            setMealVoucher(null);
            setSplitInfo(null);
            setAdjustmentModal(null);
            setAdjustmentForm(buildDefaultAdjustmentForm());
            await Promise.all([loadDashboard(), loadSummary()]);
            setSelectedOrderId(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile registrare il pagamento.';
            setBanner({ type: 'error', message });
        } finally {
            setOrderSubmitting(false);
        }
    }, [selectedOrder, pendingPaymentAction, buildOrderUpdatePayload, paymentSummary, loadDashboard, loadSummary, showPaymentConfirmation, setBanner]);

    const handleCancelOrder = useCallback(async () => {
        if (!selectedOrder) {
            if (hasDraftItems) {
                setDraftItems([]);
                setPaymentInput('');
                setSelectedTableNumber(null); // Reset anche tavolo selezionato
                const message = selectedTableNumber
                    ? `Ordine per tavolo ${selectedTableNumber} annullato.`
                    : 'Conto al banco annullato.';
                setBanner({ type: 'success', message });
                window.setTimeout(() => setBanner(null), 1800);
            }
            return;
        }
        if (orderSubmitting) {
            return;
        }
        const targetLabel = selectedOrder.code ?? `ordine #${selectedOrder.id}`;
        setOrderSubmitting(true);
        try {
            const payload = buildOrderUpdatePayload();
            const hasChanges = payload.adjustments.length > 0 || payload.additions.length > 0;
            await cancelPosOrder(selectedOrder.id, hasChanges ? payload : undefined);
            setOrderAdjustments({});
            setDraftItems([]);
            setPaymentInput('');
            setDiscount(null);
            setMealVoucher(null);
            setSplitInfo(null);
            setAdjustmentModal(null);
            setAdjustmentForm(buildDefaultAdjustmentForm());
            setSelectedTableNumber(null); // Reset anche qui
            setBanner({ type: 'success', message: `Ordine ${targetLabel} annullato.` });
            window.setTimeout(() => setBanner(null), 2500);
            await loadDashboard();
            setSelectedOrderId(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile annullare l\'ordine.';
            setBanner({ type: 'error', message });
        } finally {
            setOrderSubmitting(false);
        }
    }, [selectedOrder, hasDraftItems, orderSubmitting, buildOrderUpdatePayload, loadDashboard, setBanner, selectedTableNumber]);

    const handleCreateTableOrder = useCallback(async () => {
        if (!selectedTableNumber || !hasDraftItems || orderSubmitting) {
            return;
        }

        setOrderSubmitting(true);
        try {
            const items = draftItems.map(draft => ({
                reference: typeof draft.sourceId === 'number' ? `menu-${draft.sourceId}` : draft.key,
                name: draft.name || 'Articolo',
                menuItemId: typeof draft.sourceId === 'number' ? draft.sourceId : undefined,
                price: roundCurrency(draft.price),
                quantity: Math.max(1, clampQuantity(draft.quantity)),
                custom: Boolean(draft.isCustom || draft.sourceId == null),
            }));

            const payload: CreatePosOrderPayload = {
                tableNumber: selectedTableNumber,
                customerName: undefined,
                items,
            };

            const newOrder = await createPosOrder(payload);

            setDraftItems([]);
            setPaymentInput('');
            setDiscount(null);
            setMealVoucher(null);
            setSplitInfo(null);
            setSelectedTableNumber(null);

            setBanner({ type: 'success', message: `Ordine creato per tavolo ${selectedTableNumber}` });
            window.setTimeout(() => setBanner(null), 2500);

            await loadDashboard();

            // Seleziona il nuovo ordine
            setSelectedOrderId(newOrder.id);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile creare l\'ordine.';
            setBanner({ type: 'error', message });
        } finally {
            setOrderSubmitting(false);
        }
    }, [selectedTableNumber, hasDraftItems, orderSubmitting, draftItems, loadDashboard, setBanner]);

    const quickActions = [
        {
            key: 'cash',
            label: 'Incasso contanti',
            description: 'Registra rapidamente un incasso in contanti.',
            icon: <FiDollarSign />,
            onClick: () => triggerManualModal({
                method: 'CASH',
                title: 'Incasso contanti',
                description: 'Registra un pagamento ricevuto in contanti.',
            }),
        },
        {
            key: 'pos',
            label: 'Incasso POS',
            description: 'Registra un incasso con carta o bancomat.',
            icon: <FiCreditCard />,
            onClick: () => triggerManualModal({
                method: 'POS',
                title: 'Incasso POS',
                description: 'Registra un pagamento effettuato tramite terminale POS.',
            }),
        },
        {
            key: 'orders',
            label: 'Gestisci ordini',
            description: 'Apri la board ordini per consegne e sala.',
            icon: <FiShoppingBag />,
            onClick: () => navigate('/orders'),
            disabled: !orderBoardEnabled,
        },
        {
            key: 'cashflow',
            label: 'Riepilogo cassa',
            description: 'Accedi al dettaglio movimenti e report.',
            icon: <FiBarChart2 />,
            onClick: () => navigate('/cashflow'),
            disabled: !cashflowEnabled,
        },
        {
            key: 'print',
            label: 'Centro stampa',
            description: 'Avvia ristampe e monitoraggio code.',
            icon: <FiPrinter />,
            onClick: () => navigate('/print-center'),
            disabled: !printCenterEnabled,
        },
        {
            key: 'refresh',
            label: 'Aggiorna dati',
            description: 'Ricarica sessioni e riepilogo incassi.',
            icon: <FiRefreshCcw />,
            onClick: () => {
                loadSessions();
                loadSummary();
                loadDashboard();
            },
        },
    ];

    return (
        <div className="pos-page pos-page--app">
            <header className="pos-app-bar">
                <div className="pos-app-bar__left">
                    <button
                        type="button"
                        className={`pos-app-bar__status ${activeSession ? 'pos-app-bar__status--active' : 'pos-app-bar__status--inactive'}`}
                        onClick={() => setShowSessionPanel(!showSessionPanel)}
                        title={activeSession ? 'Sessione aperta' : 'Nessuna sessione'}
                    >
                        {activeSession ? <FiUnlock /> : <FiLock />}
                        <span>{activeSession ? `Sessione: ${activeSession.openedBy ?? 'Attiva'}` : 'Sessione chiusa'}</span>
                    </button>
                    <button
                        type="button"
                        className="pos-app-bar__stats"
                        onClick={() => setShowStatsPanel(!showStatsPanel)}
                        title="Andamento giornata"
                    >
                        <FiBarChart2 />
                        <span>{dashboardStats.todaysTotal} oggi • {dashboardStats.openOrders} ordini</span>
                    </button>
                </div>
                <div className="pos-app-bar__actions">
                    <button
                        type="button"
                        className="pos-app-bar__action"
                        onClick={() => setShowQuickActions(!showQuickActions)}
                        title="Azioni rapide"
                    >
                        <FiPlusCircle />
                    </button>
                    <button
                        type="button"
                        className={`pos-app-bar__action ${soundEnabled ? 'pos-app-bar__action--active' : ''}`}
                        onClick={toggleSound}
                        title={soundEnabled ? 'Disattiva notifiche sonore' : 'Attiva notifiche sonore'}
                    >
                        {soundEnabled ? <FiVolume2 /> : <FiVolumeX />}
                    </button>
                    <button
                        type="button"
                        className={`pos-app-bar__action ${priceMode === 'table' ? 'pos-app-bar__action--active' : ''}`}
                        onClick={() => {
                            const newMode = priceMode === 'counter' ? 'table' : 'counter';
                            setPriceMode(newMode);
                            localStorage.setItem('pos-price-mode', newMode);
                        }}
                        title={priceMode === 'counter' ? 'Modalità Banco (clicca per Tavolo)' : 'Modalità Tavolo (clicca per Banco)'}
                    >
                        {priceMode === 'counter' ? '🏪' : '🪑'}
                    </button>
                    <button
                        type="button"
                        className="pos-app-bar__action"
                        onClick={() => {
                            loadSessions();
                            loadSummary();
                            loadDashboard();
                        }}
                        title="Aggiorna tutto"
                    >
                        <FiRefreshCcw />
                    </button>
                </div>
            </header>

            {banner && (
                <div className={`pos-banner pos-banner--${banner.type}`} role="status">
                    {banner.message}
                </div>
            )}

            {showSessionPanel && (
                <div className="pos-dropdown-panel">
                    <div className="pos-dropdown-panel__content">
                        <header className="pos-dropdown-panel__header">
                            <h3>Stato sessione</h3>
                            <button
                                type="button"
                                className="pos-dropdown-panel__close"
                                onClick={() => setShowSessionPanel(false)}
                                aria-label="Chiudi"
                            >
                                <FiXCircle />
                            </button>
                        </header>
                        <div className="pos-dropdown-panel__body">
                            {sessionsLoading ? (
                                <p>Caricamento…</p>
                            ) : activeSession ? (
                                <div className="pos-session-info">
                                    <div className="pos-session-info__row">
                                        <span>Aperta da:</span>
                                        <strong>{activeSession.openedBy ?? '—'}</strong>
                                    </div>
                                    <div className="pos-session-info__row">
                                        <span>Fondo iniziale:</span>
                                        <strong>{formatCurrency(activeSession.openingFloat)}</strong>
                                    </div>
                                    <div className="pos-session-info__row">
                                        <span>Incassato:</span>
                                        <strong>{formatCurrency(activeSession.paymentsAmount)}</strong>
                                    </div>
                                    <button
                                        type="button"
                                        className="pos-dropdown-panel__button pos-dropdown-panel__button--danger"
                                        onClick={handleCloseSession}
                                        disabled={busy}
                                    >
                                        <FiLock />
                                        <span>Chiudi sessione</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="pos-session-info">
                                    <p>Nessuna sessione attiva. Apri una sessione per iniziare.</p>
                                    <button
                                        type="button"
                                        className="pos-dropdown-panel__button"
                                        onClick={handleOpenSession}
                                        disabled={busy}
                                    >
                                        <FiUnlock />
                                        <span>Apri sessione</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showStatsPanel && (
                <div className="pos-dropdown-panel">
                    <div className="pos-dropdown-panel__content">
                        <header className="pos-dropdown-panel__header">
                            <h3>Andamento della giornata</h3>
                            <button
                                type="button"
                                className="pos-dropdown-panel__close"
                                onClick={() => setShowStatsPanel(false)}
                                aria-label="Chiudi"
                            >
                                <FiXCircle />
                            </button>
                        </header>
                        <div className="pos-dropdown-panel__body">
                            {summaryLoading ? (
                                <p>Caricamento…</p>
                            ) : summaryError ? (
                                <p className="pos-error">{summaryError}</p>
                            ) : (
                                <div className="pos-summary-panel">
                                    <div className="pos-summary-filters">
                                        <label className="pos-summary-filters__field">
                                            <span>Periodo</span>
                                            <select
                                                value={summaryFilterMode}
                                                onChange={(event) => setSummaryFilterMode(event.target.value as SummaryFilterMode)}
                                            >
                                                <option value="today">Oggi</option>
                                                <option value="activeSession" disabled={!activeSession}>Sessione attiva</option>
                                                <option value="session" disabled={selectableSessions.length === 0}>Sessione specifica</option>
                                                <option value="range">Intervallo personalizzato</option>
                                            </select>
                                        </label>
                                        {summaryFilterMode === 'session' && (
                                            <label className="pos-summary-filters__field">
                                                <span>Sessione</span>
                                                <select
                                                    value={summaryFilterSessionId ?? ''}
                                                    onChange={(event) => setSummaryFilterSessionId(event.target.value ? Number(event.target.value) : null)}
                                                >
                                                    <option value="">Seleziona sessione</option>
                                                    {selectableSessions.map(session => {
                                                        const opened = formatDateTimeCompact(session.openedAt ?? undefined);
                                                        const closed = session.closedAt
                                                            ? formatDateTimeCompact(session.closedAt)
                                                            : session.open
                                                                ? 'In corso'
                                                                : '—';
                                                        return (
                                                            <option key={session.id} value={session.id}>
                                                                #{session.id} • {opened} → {closed}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </label>
                                        )}
                                        {summaryFilterMode === 'range' && (
                                            <div className="pos-summary-filters__range">
                                                <label className="pos-summary-filters__field">
                                                    <span>Dal</span>
                                                    <input
                                                        type="date"
                                                        value={summaryRangeFrom}
                                                        onChange={(event) => setSummaryRangeFrom(event.target.value)}
                                                    />
                                                </label>
                                                <label className="pos-summary-filters__field">
                                                    <span>Al</span>
                                                    <input
                                                        type="date"
                                                        value={summaryRangeTo}
                                                        onChange={(event) => setSummaryRangeTo(event.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="pos-summary-filters__reload"
                                            onClick={loadSummary}
                                            disabled={summaryLoading}
                                        >
                                            <FiRefreshCcw />
                                            <span>Aggiorna</span>
                                        </button>
                                    </div>
                                    <div className="pos-summary-context">
                                        <div className="pos-summary-context__texts">
                                            <strong>{summaryConfig.title}</strong>
                                            <span>{summaryConfig.subtitle}</span>
                                        </div>
                                        {summaryConfig.notice && (
                                            <span className="pos-summary-context__notice">{summaryConfig.notice}</span>
                                        )}
                                    </div>
                                    <div className="pos-stats-grid">
                                        {summaryCards.map(card => (
                                            <div key={card.key} className="pos-stat-card">
                                                <span className="pos-stat-card__label">{card.label}</span>
                                                <strong className="pos-stat-card__value">{card.value}</strong>
                                            </div>
                                        ))}
                                    </div>
                                    <section className="pos-corrispettivo">
                                        <header className="pos-corrispettivo__header">
                                            <div>
                                                <h4>Corrispettivo</h4>
                                                <p>Numeri utili per la chiusura di cassa e la stampa del documento.</p>
                                            </div>
                                            <button
                                                type="button"
                                                className="pos-corrispettivo__export"
                                                onClick={handleExportCorrispettivo}
                                                disabled={!summary}
                                            >
                                                <FiPrinter />
                                                <span>Esporta riepilogo</span>
                                            </button>
                                        </header>
                                        <div className="pos-corrispettivo__grid">
                                            <div>
                                                <span>Totale incassi</span>
                                                <strong>{formatCurrency(corrispettivoData.total)}</strong>
                                            </div>
                                            <div>
                                                <span>Contanti</span>
                                                <strong>{formatCurrency(corrispettivoData.cash)}</strong>
                                            </div>
                                            <div>
                                                <span>Elettronici</span>
                                                <strong>{formatCurrency(corrispettivoData.electronic)}</strong>
                                            </div>
                                            <div>
                                                <span>Pagamenti</span>
                                                <strong>{corrispettivoData.payments}</strong>
                                            </div>
                                            <div>
                                                <span>Ordini chiusi</span>
                                                <strong>{corrispettivoData.orders}</strong>
                                            </div>
                                            <div>
                                                <span>Stima IVA</span>
                                                <strong>{formatCurrency(corrispettivoData.vat)}</strong>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showQuickActions && (
                <div className="pos-dropdown-panel">
                    <div className="pos-dropdown-panel__content">
                        <header className="pos-dropdown-panel__header">
                            <h3>Azioni rapide</h3>
                            <button
                                type="button"
                                className="pos-dropdown-panel__close"
                                onClick={() => setShowQuickActions(false)}
                                aria-label="Chiudi"
                            >
                                <FiXCircle />
                            </button>
                        </header>
                        <div className="pos-dropdown-panel__body">
                            <div className="pos-quick-actions-list">
                                {quickActions.map(action => (
                                    <button
                                        key={action.key}
                                        type="button"
                                        className="pos-quick-action-item"
                                        onClick={() => {
                                            action.onClick();
                                            setShowQuickActions(false);
                                        }}
                                        disabled={Boolean(action.disabled)}
                                    >
                                        <span className="pos-quick-action-item__icon">{action.icon}</span>
                                        <div className="pos-quick-action-item__text">
                                            <strong>{action.label}</strong>
                                            <small>{action.description}</small>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ TAB BUTTONS (Tavoli / Ordini / Nuovo Conto) ============ */}
            <div className="pos-tabs-container">
                <TabButton
                    title="Tavoli"
                    icon="🍽️"
                    metrics={tablesMetrics}
                    active={activeTab === 'tables'}
                    onClick={() => setActiveTab('tables')}
                />
                <TabButton
                    title="Ordini Aperti"
                    icon="📋"
                    metrics={ordersMetrics}
                    active={activeTab === 'orders'}
                    onClick={() => setActiveTab('orders')}
                />
                <TabButton
                    title="Nuovo Conto al Banco"
                    icon="🛒"
                    metrics={[]}
                    active={activeTab === 'counter'}
                    onClick={() => {
                        setSelectedOrderId(null);
                        setSelectedTableNumber(null);
                        setDraftItems([]);
                        setPaymentInput('');
                        setDiscount(null);
                        setMealVoucher(null);
                        setSplitInfo(null);
                        setActiveTab('counter');
                    }}
                />
            </div>
            {/* ======================================================= */}

            {/* 📦 CATALOGO: sempre visibile tra i tab e il workspace */}
            <section className="pos-menu">
                <header className="pos-menu__header">
                    <h2>Catalogo</h2>
                    <div className="pos-menu__toolbar">
                        {menuHasItems && (
                            <div className="pos-menu__search">
                                <input
                                    type="search"
                                    placeholder="Cerca prodotto"
                                    value={productSearch}
                                    onChange={handleSearchChange}
                                    onFocus={handleSearchFocus}
                                />
                                {productSearch.length > 0 && (
                                    <button
                                        type="button"
                                        className="pos-menu__search-clear"
                                        onClick={handleClearSearch}
                                        aria-label="Azzera ricerca"
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="pos-menu__quick-actions">
                            <button
                                type="button"
                                className="pos-menu__custom-button"
                                onClick={openCustomItemModal}
                            >
                                <FiPlusCircle />
                                <span>Articolo libero</span>
                            </button>
                            <button
                                type="button"
                                className="pos-menu__custom-button"
                                onClick={openCoverChargeModal}
                                style={{ background: '#10b981', borderColor: '#059669' }}
                                title={`Coperto: €${coverChargeAmount}`}
                            >
                                🍽️
                                <span>Coperto ({coverChargeAmount > 0 ? `€${coverChargeAmount.toFixed(2)}` : 'non configurato'})</span>
                            </button>
                        </div>
                    </div>
                </header>
                {dashboardLoading ? (
                    <p className="pos-workspace__placeholder">Caricamento menu…</p>
                ) : dashboardError ? (
                    <p className="pos-workspace__error">{dashboardError}</p>
                ) : dashboard && menuHasItems ? (
                    <div className={`pos-menu__body${catalogPanelOpen ? ' pos-menu__body--panel-open' : ''}`}>
                        <div className="pos-menu__categories-grid">
                            {categoryOptions.map(option => {
                                const isActive = !isSearching && catalogPanelOpen && selectedCategoryKey === option.key;
                                const tileStyle: React.CSSProperties = {
                                    '--cat-gradient': option.theme.headerGradient,
                                    '--cat-text': option.theme.headerText,
                                } as React.CSSProperties;
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        className={`pos-category-tile${isActive ? ' pos-category-tile--active' : ''}`}
                                        style={tileStyle}
                                        onClick={() => handleSelectCategory(option.key)}
                                        title={option.description || option.label}
                                    >
                                        <span className="pos-category-tile__name">{option.label}</span>
                                        <span className="pos-category-tile__count">{option.items.length}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {catalogPanelOpen && (
                            <div className="pos-menu__panel">
                                <header className="pos-menu__panel-header">
                                    <div>
                                        <h3 className="pos-menu__panel-title">
                                            {isSearching
                                                ? `Risultati per "${productSearch.trim()}"`
                                                : activeCategory?.label ?? 'Catalogo'}
                                        </h3>
                                        {!isSearching && activeCategory?.description && (
                                            <p className="pos-menu__panel-subtitle">{activeCategory.description}</p>
                                        )}
                                        {isSearching && (
                                            <p className="pos-menu__panel-subtitle">
                                                {filteredCatalogItems.length === 0
                                                    ? 'Nessun risultato nella ricerca corrente.'
                                                    : `${filteredCatalogItems.length} risultato${filteredCatalogItems.length === 1 ? '' : 'i'} trovati.`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="pos-menu__panel-actions">
                                        {!isSearching && activeCategory && (
                                            <span className="pos-menu__panel-count">{activeCategory.items.length} articoli</span>
                                        )}
                                        <button
                                            type="button"
                                            className="pos-menu__panel-close"
                                            onClick={handleCloseCatalogPanel}
                                        >
                                            Chiudi
                                        </button>
                                    </div>
                                </header>
                                <div className="pos-menu__panel-grid">
                                    {displayedCatalogItems.length === 0 ? (
                                        <p className="pos-menu__panel-empty">Nessun articolo da mostrare.</p>
                                    ) : (
                                        displayedCatalogItems.map((item, index) => {
                                            const key = item.id ?? `${item.__category.key}-${item.name ?? index}`;
                                            const priceLabel = formatCurrency(item.price);
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className="pos-menu__item-card"
                                                    onClick={() => handleSelectCatalogItem(item)}
                                                >
                                                    <div className="pos-menu__item-header">
                                                        <span className="pos-menu__item-name">{item.name ?? 'Articolo'}</span>
                                                        <span className="pos-menu__item-price">{priceLabel}</span>
                                                    </div>
                                                    {item.description && (
                                                        <p className="pos-menu__item-description">{item.description}</p>
                                                    )}
                                                    <span className="pos-menu__item-badge">{item.__category.label}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="pos-workspace__placeholder">Configura il menu per visualizzare gli articoli POS.</p>
                )}
            </section>

            <section className="pos-workspace">
                {/* COLONNA SINISTRA: Area dinamica (Tavoli O Ordini O vuota) */}
                <div className="pos-workspace__column">
                    <div className={`pos-workspace__column--tables ${activeTab !== 'tables' ? 'pos-workspace--hidden' : ''}`}>
                        <header className="pos-workspace__header">
                            <h2>Tavoli</h2>
                        </header>
                        {dashboardLoading ? (
                            <p className="pos-workspace__placeholder">Caricamento tavoli…</p>
                        ) : dashboardError ? (
                            <p className="pos-workspace__error">{dashboardError}</p>
                        ) : (
                            <>
                                {/* Filtri Zona + Filtro Stato sulla stessa riga */}
                                <div className="pos-table-tabs">
                                    <button
                                        type="button"
                                        className={`pos-table-tab ${tableGroupFilter === 'all' ? 'pos-table-tab--active' : ''}`}
                                        onClick={() => setTableGroupFilter('all')}
                                    >
                                        Tutti
                                    </button>
                                    {tableGroups.map(group => (
                                        <button
                                            key={group.key}
                                            type="button"
                                            className={`pos-table-tab ${tableGroupFilter === group.key ? 'pos-table-tab--active' : ''}`}
                                            onClick={() => setTableGroupFilter(group.key)}
                                        >
                                            {group.label}
                                        </button>
                                    ))}

                                    {/* Pulsante Filtro Stato con Dropdown */}
                                    <div className="pos-table-filter-wrapper">
                                        <button
                                            type="button"
                                            className={`pos-table-tab pos-table-tab--filter ${tableStatusFilter !== 'all' ? 'pos-table-tab--active' : ''}`}
                                            onClick={() => setShowStatusFilterDropdown(!showStatusFilterDropdown)}
                                        >
                                            <FiFilter />
                                            <span>Filtro</span>
                                        </button>

                                        {/* Dropdown Filtri Stato */}
                                        {showStatusFilterDropdown && (
                                            <div className="pos-table-filter-dropdown">
                                                <button
                                                    type="button"
                                                    className={`pos-table-filter-option ${tableStatusFilter === 'free' ? 'pos-table-filter-option--active' : ''}`}
                                                    onClick={() => {
                                                        setTableStatusFilter('free');
                                                        setShowStatusFilterDropdown(false);
                                                    }}
                                                >
                                                    ✓ Liberi
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pos-table-filter-option ${tableStatusFilter === 'occupied' ? 'pos-table-filter-option--active' : ''}`}
                                                    onClick={() => {
                                                        setTableStatusFilter('occupied');
                                                        setShowStatusFilterDropdown(false);
                                                    }}
                                                >
                                                    ✏️ Occupati
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pos-table-filter-option ${tableStatusFilter === 'checkout' ? 'pos-table-filter-option--active' : ''}`}
                                                    onClick={() => {
                                                        setTableStatusFilter('checkout');
                                                        setShowStatusFilterDropdown(false);
                                                    }}
                                                >
                                                    💳 Richiesta Pagamento
                                                </button>
                                                <button
                                                    type="button"
                                                    className="pos-table-filter-option pos-table-filter-option--reset"
                                                    onClick={() => {
                                                        setTableStatusFilter('all');
                                                        setShowStatusFilterDropdown(false);
                                                    }}
                                                >
                                                    ↺ Rimuovi Filtro
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="pos-tables-grid-wrapper">
                                    {tableGroups
                                        .filter(group => tableGroupFilter === 'all' || tableGroupFilter === group.key)
                                        .map(group => (
                                            <div key={group.key} className="pos-tables-grid">
                                                {group.tables
                                                    .filter(({ table }) => {
                                                        // Applica filtro stato
                                                        if (tableStatusFilter === 'all') return true;

                                                        const hasCheckoutRequest = table.currentOrder?.status === 'CHECKOUT_REQUESTED';

                                                        if (tableStatusFilter === 'free') {
                                                            return !table.occupied && table.status !== 'CLOSED';
                                                        }
                                                        if (tableStatusFilter === 'occupied') {
                                                            return table.occupied && !hasCheckoutRequest;
                                                        }
                                                        if (tableStatusFilter === 'checkout') {
                                                            return hasCheckoutRequest;
                                                        }

                                                        return true;
                                                    })
                                                    .map(({ key, table, displayNumber, isPlaceholder }) => {
                                                        const variant: TableCardVariant = table.occupied
                                                            ? 'occupied'
                                                            : table.status === 'CLOSED'
                                                                ? 'closed'
                                                                : 'free';
                                                        const isSelected = table.currentOrder && table.currentOrder.id === selectedOrderId;
                                                        const totalValue = table.currentOrder
                                                            ? formatCurrency(table.currentOrder.total ?? 0)
                                                            : null;
                                                        const hasCheckoutRequest = table.currentOrder?.status === 'CHECKOUT_REQUESTED';
                                                        const paymentMethod = table.currentOrder?.status === 'CHECKOUT_REQUESTED'
                                                            ? (table.currentOrder as any).paymentMethod
                                                            : null;
                                                        return (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                className={[
                                                                    'pos-table-card',
                                                                    `pos-table-card--${variant}`,
                                                                    isSelected ? 'pos-table-card--selected' : null,
                                                                    isPlaceholder ? 'pos-table-card--placeholder' : null,
                                                                    hasCheckoutRequest ? 'pos-table-card--checkout-request' : null,
                                                                ].filter(Boolean).join(' ')}
                                                                onClick={() => handleSelectTable(table)}
                                                                title={`Tavolo ${displayNumber}${totalValue ? ` - ${totalValue}` : ''}${hasCheckoutRequest ? ' - Richiesta pagamento' : ''}`}
                                                            >
                                                                {/* Status Icon */}
                                                                <div className="pos-table-card__icon">
                                                                    {variant === 'free' && <span className="pos-table-card__status-icon">✓</span>}
                                                                    {variant === 'occupied' && <span className="pos-table-card__status-icon">✏️</span>}
                                                                    {variant === 'closed' && <span className="pos-table-card__status-icon">🔒</span>}
                                                                </div>

                                                                {/* Table Number */}
                                                                <div className="pos-table-card__number">{displayNumber}</div>

                                                                {/* Items Count */}
                                                                {table.currentOrder && table.currentOrder.itemsCount && (
                                                                    <div className="pos-table-card__covers">
                                                                        {table.currentOrder.itemsCount} {table.currentOrder.itemsCount === 1 ? 'articolo' : 'articoli'}
                                                                    </div>
                                                                )}

                                                                {/* Total */}
                                                                <div className="pos-table-card__total">
                                                                    {totalValue || '€ 0,00'}
                                                                </div>

                                                                {/* Status Label */}
                                                                <div className="pos-table-card__status">
                                                                    {variant === 'free' && 'Disponibile'}
                                                                    {variant === 'occupied' && 'Occupato'}
                                                                    {variant === 'closed' && 'Chiuso'}
                                                                </div>

                                                                {/* Checkout Badge */}
                                                                {hasCheckoutRequest && (
                                                                    <span className="pos-table-card__badge" title={`Richiesta pagamento${paymentMethod ? ` - ${paymentMethod}` : ''}`}>
                                                                        💳
                                                                    </span>
                                                                )}

                                                                {/* Time */}
                                                                {table.currentOrder?.openedAt && (
                                                                    <div className="pos-table-card__time">🕐 {formatTime(table.currentOrder.openedAt)}</div>
                                                                )}

                                                                {/* Delete Button - Solo per tavoli LIBERI */}
                                                                {variant === 'free' && !isPlaceholder && table.tableNumber && (
                                                                    <button
                                                                        type="button"
                                                                        className="pos-table-card__delete"
                                                                        onClick={(e) => handleDeleteTable(table.tableNumber!, e)}
                                                                        title={`Elimina tavolo ${displayNumber}`}
                                                                        aria-label={`Elimina tavolo ${displayNumber}`}
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                    <div className={`pos-workspace__column pos-workspace__column--orders ${activeTab !== 'orders' ? 'pos-workspace--hidden' : ''}`}>
                        <header className="pos-workspace__header">
                            <h2>Ordini aperti</h2>
                        </header>
                        <div className="pos-orders">
                            <div className="pos-orders__list">
                                {dashboardLoading ? (
                                    <p className="pos-workspace__placeholder">Caricamento ordini…</p>
                                ) : dashboardError ? (
                                    <p className="pos-workspace__error">{dashboardError}</p>
                                ) : dashboard && dashboard.activeOrders && dashboard.activeOrders.length > 0 ? (
                                    dashboard.activeOrders.map((order) => {
                                        const isSelected = order.id === selectedOrderId;
                                        return (
                                            <button
                                                key={order.id}
                                                type="button"
                                                className={[
                                                    'pos-order-card',
                                                    order.paid ? 'pos-order-card--paid' : 'pos-order-card--open',
                                                    isSelected ? 'pos-order-card--selected' : '',
                                                ].join(' ').trim()}
                                                onClick={() => handleSelectOrder(order)}
                                            >
                                                <div className="pos-order-card__header">
                                                    <strong>{order.code ?? `Ordine #${order.id}`}</strong>
                                                    <span>{order.tableNumber ?? 'N/D'}</span>
                                                </div>
                                                <div className="pos-order-card__meta">
                                                    <span>{order.itemsCount ?? 0} articoli</span>
                                                    {order.customerName && <span>{order.customerName}</span>}
                                                    {order.openedAt && (
                                                        <span className="pos-order-card__time">
                                                            🕐 {formatTime(order.openedAt)}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="pos-order-card__total">{formatCurrency(order.total)}</span>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <p className="pos-workspace__placeholder">Nessun ordine aperto al momento.</p>
                                )}
                            </div>
                            <div className="pos-orders__detail">
                                <header className="pos-orders__detail-header">
                                    <div className="pos-orders__detail-heading">
                                        <h3>Dettaglio ordine</h3>
                                        {orderStatusLabel && (
                                            <span className="pos-orders__detail-tag">{orderStatusLabel}</span>
                                        )}
                                    </div>
                                    {(selectedOrder || hasDraftItems) && (
                                        <button
                                            type="button"
                                            className="pos-orders__detail-action"
                                            onClick={handleCancelOrder}
                                            disabled={orderSubmitting}
                                        >
                                            <FiXCircle />
                                            <span>{selectedOrder ? 'Annulla ordine' : 'Annulla conto'}</span>
                                        </button>
                                    )}
                                </header>
                                {orderLoading ? (
                                    <p className="pos-workspace__placeholder">Caricamento dettaglio…</p>
                                ) : orderError ? (
                                    <p className="pos-workspace__error">{orderError}</p>
                                ) : (selectedOrder || hasDraftItems) ? (
                                    <div className="pos-order-shell">
                                        <div className="pos-order-detail pos-order-shell__primary">
                                            <div className="pos-order-detail__meta">
                                                <span className="pos-order-detail__code">{orderTitleLabel || 'Dettaglio ordine'}</span>
                                                {orderSecondaryLabel && (
                                                    <span className="pos-order-detail__table">{orderSecondaryLabel}</span>
                                                )}
                                                {selectedOrder?.customerName && (
                                                    <span className="pos-order-detail__customer">{selectedOrder.customerName}</span>
                                                )}
                                            </div>
                                            <div className="pos-order-shell__scroll">
                                                <ul className="pos-order-detail__items">
                                                    {orderItemsForDisplay.map(detail => {
                                                        const quantityChanged = detail.quantityChanged;
                                                        const removed = detail.removed;
                                                        const quantityClassNames = [
                                                            'pos-order-detail__controls-quantity',
                                                            quantityChanged ? 'pos-order-detail__controls-quantity--changed' : '',
                                                            removed ? 'pos-order-detail__controls-quantity--removed' : '',
                                                        ].filter(Boolean).join(' ');
                                                        const nameClassNames = [
                                                            'pos-order-detail__item-name',
                                                            removed ? 'pos-order-detail__item-name--removed' : '',
                                                        ].filter(Boolean).join(' ');
                                                        const unitLabel = formatCurrency(detail.unitPrice);
                                                        return (
                                                            <li key={detail.key} className="pos-order-detail__item">
                                                                <div>
                                                                    <strong className={nameClassNames}>{detail.name}</strong>
                                                                    {detail.modifiers.length > 0 && (
                                                                        <small className="pos-order-detail__item-note">{detail.modifiers.join(', ')}</small>
                                                                    )}
                                                                    {!removed && (
                                                                        <small className="pos-order-detail__item-note">Prezzo unitario {unitLabel}</small>
                                                                    )}
                                                                    {detail.comment && (
                                                                        <small className="pos-order-detail__item-comment">
                                                                            <span aria-hidden="true">📝</span>
                                                                            <span>{detail.comment}</span>
                                                                        </small>
                                                                    )}
                                                                    {removed && (
                                                                        <small className="pos-order-detail__item-removed">Articolo rimosso</small>
                                                                    )}
                                                                </div>
                                                                <div className="pos-order-detail__controls">
                                                                    <div className="pos-order-detail__controls-group">
                                                                        <button
                                                                            type="button"
                                                                            className="pos-order-detail__controls-button"
                                                                            onClick={() => adjustOrderItemQuantity(detail.key, detail.originalQuantity, -1)}
                                                                            disabled={detail.quantity <= 0}
                                                                            aria-label={`Diminuisci ${detail.name}`}
                                                                        >
                                                                            <FiMinus />
                                                                        </button>
                                                                        <span className={quantityClassNames}>
                                                                            x{detail.quantity}
                                                                            {quantityChanged && !removed && (
                                                                                <small>Orig. x{detail.originalQuantity}</small>
                                                                            )}
                                                                            {removed && <small>Rimosso</small>}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            className="pos-order-detail__controls-button"
                                                                            onClick={() => adjustOrderItemQuantity(detail.key, detail.originalQuantity, 1)}
                                                                            disabled={detail.quantity >= 99}
                                                                            aria-label={`Aumenta ${detail.name}`}
                                                                        >
                                                                            <FiPlus />
                                                                        </button>
                                                                    </div>
                                                                    <span className="pos-order-detail__controls-total">{formatCurrency(detail.adjustedTotal)}</span>
                                                                    <button
                                                                        type="button"
                                                                        className="pos-order-detail__controls-remove"
                                                                        onClick={() => handleRemoveOrderItem(detail.key)}
                                                                        aria-label={`Rimuovi ${detail.name}`}
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                    {hasDraftItems && selectedOrder && hasOrderItems && (
                                                        <li className="pos-order-detail__divider" aria-hidden="true">Aggiunte correnti</li>
                                                    )}
                                                    {hasDraftItems && draftItems.map(draft => {
                                                        const isVariant = Boolean(draft.isVariant);
                                                        const displayName = draft.name || 'Articolo';
                                                        const unitPrice = roundCurrency(draft.price);
                                                        const draftTotal = formatCurrency(roundCurrency(unitPrice * draft.quantity));
                                                        const unitLabel = formatCurrency(unitPrice);
                                                        const variantOptions = !isVariant && typeof draft.sourceId === 'number'
                                                            ? (menuItemVariantsById.get(draft.sourceId) ?? [])
                                                            : [];
                                                        const selectValue = variantDraftSelections[draft.key] ?? '';
                                                        const itemClassName = `pos-order-detail__item pos-order-detail__item--draft${isVariant ? ' pos-order-detail__item--variant' : ''}`;
                                                        const variantItemStyle = isVariant
                                                            ? { background: '#f9fafb', borderLeft: '3px solid #d1d5db', paddingLeft: '12px' } as React.CSSProperties
                                                            : undefined;
                                                        return (
                                                            <li key={draft.key} className={itemClassName} style={variantItemStyle}>
                                                                <div>
                                                                    <strong className="pos-order-detail__item-name">{displayName}</strong>
                                                                    <small className="pos-order-detail__item-note">
                                                                        {draft.isCustom
                                                                            ? 'Articolo personalizzato'
                                                                            : isVariant
                                                                                ? 'Variante aggiuntiva'
                                                                                : `Prezzo unitario ${unitLabel}`}
                                                                    </small>
                                                                    {variantOptions.length > 0 && (
                                                                        <label className="pos-order-detail__variant-selector">
                                                                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Aggiungi variante</span>
                                                                            <select
                                                                                value={selectValue}
                                                                                onChange={(event) => handleDraftVariantSelect(draft.key, event.target.value)}
                                                                                className="pos-order-detail__variant-select"
                                                                                style={{
                                                                                    width: '100%',
                                                                                    padding: '6px 10px',
                                                                                    borderRadius: '8px',
                                                                                    border: '1px solid #d1d5db',
                                                                                    fontSize: '0.9rem',
                                                                                }}
                                                                            >
                                                                                <option value="">+ Aggiungi variante</option>
                                                                                {variantOptions.map(option => {
                                                                                    const optionKey = buildVariantKey(option);
                                                                                    const optionName = (option.name ?? '').trim() || 'Variante';
                                                                                    const optionPrice = roundCurrency(toNumber(option.price));
                                                                                    const optionLabel = `${optionName} – ${formatCurrency(optionPrice > 0 ? optionPrice : unitPrice)}`;
                                                                                    return (
                                                                                        <option key={optionKey} value={optionKey}>{optionLabel}</option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </label>
                                                                    )}
                                                                </div>
                                                                <div className="pos-order-detail__controls">
                                                                    <div className="pos-order-detail__controls-group">
                                                                        <button
                                                                            type="button"
                                                                            className="pos-order-detail__controls-button"
                                                                            onClick={() => handleDecrementDraftItem(draft.key)}
                                                                            disabled={draft.quantity <= 1}
                                                                            aria-label={`Diminuisci ${displayName}`}
                                                                        >
                                                                            <FiMinus />
                                                                        </button>
                                                                        <span className="pos-order-detail__controls-quantity">x{draft.quantity}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="pos-order-detail__controls-button"
                                                                            onClick={() => handleIncrementDraftItem(draft.key)}
                                                                            aria-label={`Aumenta ${displayName}`}
                                                                        >
                                                                            <FiPlus />
                                                                        </button>
                                                                    </div>
                                                                    <span className="pos-order-detail__controls-total">{draftTotal}</span>
                                                                    <button
                                                                        type="button"
                                                                        className="pos-order-detail__controls-remove"
                                                                        onClick={() => handleRemoveDraftItem(draft.key)}
                                                                        aria-label={`Rimuovi ${displayName}`}
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                    {selectedOrder && !hasOrderItems && !hasDraftItems && (
                                                        <li className="pos-order-detail__empty">L'ordine non contiene articoli.</li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="pos-workspace__placeholder">Seleziona un ordine oppure aggiungi articoli dal catalogo per iniziare un nuovo conto.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* COLONNA COUNTER: Solo dettaglio ordine per conti al banco */}
                    <div className={`pos-workspace__column ${activeTab !== 'counter' ? 'pos-workspace--hidden' : ''}`}>
                        <header className="pos-workspace__header">
                            <h2>Conto al Banco</h2>
                        </header>
                        <div className="pos-orders__detail" style={{ height: '100%' }}>
                            <header className="pos-orders__detail-header">
                                <div className="pos-orders__detail-heading">
                                    <h3>Dettaglio ordine</h3>
                                </div>
                                {hasDraftItems && (
                                    <button
                                        type="button"
                                        className="pos-orders__detail-action"
                                        onClick={handleCancelOrder}
                                        disabled={orderSubmitting}
                                    >
                                        <FiXCircle />
                                        <span>Annulla conto</span>
                                    </button>
                                )}
                            </header>
                            {hasDraftItems ? (
                                <div className="pos-order-shell">
                                    <div className="pos-order-detail pos-order-shell__primary">
                                        <div className="pos-order-detail__meta">
                                            <span className="pos-order-detail__code">Nuovo Conto al Banco</span>
                                        </div>
                                        <div className="pos-order-shell__scroll">
                                            <ul className="pos-order-detail__items">
                                                {draftItems.map(draft => {
                                                    const isVariant = Boolean(draft.isVariant);
                                                    const displayName = draft.name || 'Articolo';
                                                    const unitPrice = roundCurrency(draft.price);
                                                    const draftTotal = formatCurrency(roundCurrency(unitPrice * draft.quantity));
                                                    const unitLabel = formatCurrency(unitPrice);
                                                    const variantOptions = !isVariant && typeof draft.sourceId === 'number'
                                                        ? (menuItemVariantsById.get(draft.sourceId) ?? [])
                                                        : [];
                                                    const selectValue = variantDraftSelections[draft.key] ?? '';
                                                    const itemClassName = `pos-order-detail__item pos-order-detail__item--draft${isVariant ? ' pos-order-detail__item--variant' : ''}`;
                                                    const variantItemStyle = isVariant
                                                        ? { background: '#f9fafb', borderLeft: '3px solid #d1d5db', paddingLeft: '12px' } as React.CSSProperties
                                                        : undefined;
                                                    return (
                                                        <li key={draft.key} className={itemClassName} style={variantItemStyle}>
                                                            <div>
                                                                <strong className="pos-order-detail__item-name">{displayName}</strong>
                                                                <small className="pos-order-detail__item-note">
                                                                    {draft.isCustom
                                                                        ? 'Articolo personalizzato'
                                                                        : isVariant
                                                                            ? 'Variante aggiuntiva'
                                                                            : `Prezzo unitario ${unitLabel}`}
                                                                </small>
                                                                {variantOptions.length > 0 && (
                                                                    <label className="pos-order-detail__variant-selector">
                                                                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Aggiungi variante</span>
                                                                        <select
                                                                            value={selectValue}
                                                                            onChange={(event) => handleDraftVariantSelect(draft.key, event.target.value)}
                                                                            className="pos-order-detail__variant-select"
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '6px 10px',
                                                                                borderRadius: '8px',
                                                                                border: '1px solid #d1d5db',
                                                                                fontSize: '0.9rem',
                                                                            }}
                                                                        >
                                                                            <option value="">+ Aggiungi variante</option>
                                                                            {variantOptions.map(option => {
                                                                                const optionKey = buildVariantKey(option);
                                                                                const optionName = (option.name ?? '').trim() || 'Variante';
                                                                                const optionPrice = roundCurrency(toNumber(option.price));
                                                                                const optionLabel = `${optionName} – ${formatCurrency(optionPrice > 0 ? optionPrice : unitPrice)}`;
                                                                                return (
                                                                                    <option key={optionKey} value={optionKey}>{optionLabel}</option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </label>
                                                                )}
                                                            </div>
                                                            <div className="pos-order-detail__controls">
                                                                <div className="pos-order-detail__controls-group">
                                                                    <button
                                                                        type="button"
                                                                        className="pos-order-detail__controls-button"
                                                                        onClick={() => handleDecrementDraftItem(draft.key)}
                                                                        disabled={draft.quantity <= 1}
                                                                        aria-label={`Diminuisci ${displayName}`}
                                                                    >
                                                                        <FiMinus />
                                                                    </button>
                                                                    <span className="pos-order-detail__controls-quantity">x{draft.quantity}</span>
                                                                    <button
                                                                        type="button"
                                                                        className="pos-order-detail__controls-button"
                                                                        onClick={() => handleIncrementDraftItem(draft.key)}
                                                                        aria-label={`Aumenta ${displayName}`}
                                                                    >
                                                                        <FiPlus />
                                                                    </button>
                                                                </div>
                                                                <span className="pos-order-detail__controls-total">{draftTotal}</span>
                                                                <button
                                                                    type="button"
                                                                    className="pos-order-detail__controls-remove"
                                                                    onClick={() => handleRemoveDraftItem(draft.key)}
                                                                    aria-label={`Rimuovi ${displayName}`}
                                                                >
                                                                    <FiTrash2 />
                                                                </button>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="pos-workspace__placeholder">Aggiungi articoli dal catalogo per iniziare un nuovo conto al banco.</p>
                            )}
                        </div>
                    </div>
                </div>
                {/* Fine colonna sinistra dinamica */}

                {/* COLONNA DESTRA: Solo TOTALE (NON TOCCARE) */}
                <div className="pos-workspace__column pos-workspace__column--totals">
                    <header className="pos-workspace__header">
                        <div>
                            <h2>Totale</h2>
                            <p className="pos-workspace__hint">Gestisci incasso, sconti e chiusura ordine.</p>
                        </div>
                    </header>
                    {(selectedOrder || hasDraftItems) ? (
                        <div className="pos-orders__totals">
                            <div className="pos-order-actions">
                                <div className="pos-order-actions__summary">
                                    {selectedOrder && (
                                        <div>
                                            <span>Totale iniziale</span>
                                            <strong>{paymentSummary.originalBaseFormatted}</strong>
                                        </div>
                                    )}
                                    {selectedOrder && paymentSummary.orderDelta !== 0 && (
                                        <div>
                                            <span>Correzioni ordine</span>
                                            <strong
                                                className={paymentSummary.orderDelta > 0 ? 'pos-order-summary__delta pos-order-summary__delta--positive' : 'pos-order-summary__delta pos-order-summary__delta--negative'}
                                            >
                                                {(paymentSummary.orderDelta > 0 ? '+' : '-')}{paymentSummary.orderDeltaFormatted}
                                            </strong>
                                        </div>
                                    )}
                                    {paymentSummary.additionalTotal > 0 && (
                                        <div>
                                            <span>Aggiunte correnti</span>
                                            <strong>{paymentSummary.additionalFormatted}</strong>
                                        </div>
                                    )}
                                    {paymentSummary.discountAmount > 0 && (
                                        <div>
                                            <span>Sconto</span>
                                            <strong>-{paymentSummary.discountFormatted}</strong>
                                        </div>
                                    )}
                                    {paymentSummary.voucherAmount > 0 && (
                                        <div>
                                            <span>Buoni pasto</span>
                                            <strong>-{paymentSummary.voucherFormatted}</strong>
                                        </div>
                                    )}
                                    <div className="pos-order-summary__total">
                                        <span>Da incassare</span>
                                        <strong>{paymentSummary.totalFormatted}</strong>
                                    </div>
                                    {(canAdjustPayments || selectedOrder) && (
                                        <div className="pos-order-summary__controls">
                                            {canAdjustPayments && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="pos-order-summary__chip"
                                                        onClick={() => openAdjustmentModal('customTotal')}
                                                        disabled={!canAdjustPayments}
                                                    >
                                                        {customTotalLabel}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="pos-order-summary__chip"
                                                        onClick={() => openAdjustmentModal('discount')}
                                                        disabled={!canAdjustPayments}
                                                    >
                                                        {discountLabel}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="pos-order-summary__chip"
                                                        onClick={() => openAdjustmentModal('voucher')}
                                                        disabled={!canAdjustPayments}
                                                    >
                                                        {voucherLabel}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="pos-order-summary__chip"
                                                        onClick={() => openAdjustmentModal('split')}
                                                        disabled={!canAdjustPayments}
                                                    >
                                                        {splitLabel}
                                                    </button>
                                                </>
                                            )}
                                            {selectedOrder && (
                                                <button
                                                    type="button"
                                                    className="pos-order-summary__chip pos-order-summary__chip--invoice"
                                                    onClick={toggleInvoicePanel}
                                                    disabled={!selectedOrder || invoiceLoading}
                                                >
                                                    {showInvoicePanel ? 'Chiudi fattura' : 'Fatturazione'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <span>Incasso inserito</span>
                                        <strong>{paymentSummary.inputFormatted}</strong>
                                    </div>
                                    <div>
                                        <span>Resto</span>
                                        <strong>{paymentSummary.remainingFormatted}</strong>
                                    </div>
                                    {paymentSummary.outstanding > 0 && (
                                        <div>
                                            <span>Ancora da incassare</span>
                                            <strong>{paymentSummary.outstandingFormatted}</strong>
                                        </div>
                                    )}
                                    {paymentSummary.perShare != null && (
                                        <div>
                                            <span>Quota per persona</span>
                                            <strong>{paymentSummary.perShareFormatted}</strong>
                                        </div>
                                    )}
                                </div>
                                <div className="pos-payment-buttons">
                                    {selectedTableNumber && !selectedOrder ? (
                                        // Tavolo libero - mostra pulsante per creare ordine
                                        <button
                                            type="button"
                                            className="pos-payment-button pos-payment-button--primary"
                                            onClick={handleCreateTableOrder}
                                            disabled={orderSubmitting || !hasDraftItems}
                                            style={{ gridColumn: '1 / -1', fontSize: '1.1rem', padding: '1rem' }}
                                        >
                                            📋 Salva Ordine per Tavolo {selectedTableNumber}
                                        </button>
                                    ) : (
                                        // Ordine esistente - mostra pulsanti pagamento normali
                                        <>
                                            <button
                                                type="button"
                                                className="pos-payment-button pos-payment-button--cash"
                                                onClick={() => handlePaymentAction('cash')}
                                                disabled={orderSubmitting}
                                                title="Pagamento in contanti - Stampa automatica scontrino"
                                            >
                                                💵 Contanti + 🧾
                                            </button>
                                            <button
                                                type="button"
                                                className="pos-payment-button pos-payment-button--pos"
                                                onClick={() => handlePaymentAction('pos')}
                                                disabled={orderSubmitting}
                                                title="Pagamento con carta - Stampa automatica scontrino"
                                            >
                                                💳 POS + 🧾
                                            </button>
                                            {/* RIMOSSO: Scontrino separato - ora stampa automatica con ogni pagamento */}
                                            {/* <button
                                                type="button"
                                                className="pos-payment-button pos-payment-button--ticket"
                                                onClick={() => handlePaymentAction('ticket')}
                                                disabled={orderSubmitting}
                                            >
                                                🧾 Scontrino
                                            </button> */}
                                            <button
                                                type="button"
                                                className="pos-payment-button pos-payment-button--close"
                                                onClick={() => handlePaymentAction('close')}
                                                disabled={orderSubmitting}
                                            >
                                                ✅ Chiudi ordine
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="pos-order-invoice">
                                <div className="pos-order-invoice__header">
                                    <div>
                                        <h4>Fatturazione</h4>
                                        <p>Collega un'azienda e gestisci l'invio elettronico.</p>
                                    </div>
                                    <div className="pos-order-invoice__actions">
                                        <button
                                            type="button"
                                            className="pos-order-invoice__toggle"
                                            onClick={toggleInvoicePanel}
                                            disabled={!selectedOrder || invoiceLoading}
                                        >
                                            {showInvoicePanel ? 'Nascondi pannello' : 'Apri pannello'}
                                        </button>
                                    </div>
                                </div>
                                {selectedOrder ? (
                                    <>
                                        <div className={`pos-order-invoice__status pos-order-invoice__status--${invoiceStatusMeta.variant}`}>
                                            <strong>{invoiceStatusMeta.label}</strong>
                                            <small>{invoiceStatusMeta.description}</small>
                                        </div>
                                        {invoiceError && <p className="pos-order-invoice__error">{invoiceError}</p>}
                                        {!showInvoicePanel && (
                                            <p className="pos-order-invoice__hint">Usa "Fatturazione" per cercare un'azienda e inviare il documento.</p>
                                        )}
                                        {showInvoicePanel && (
                                            <>
                                                <form className="pos-order-invoice__search" onSubmit={handleCompanySearch}>
                                                    <label>
                                                        <span>Ricerca azienda</span>
                                                        <input
                                                            type="search"
                                                            placeholder="Nome, P.IVA o codice SDI"
                                                            value={companySearch}
                                                            onChange={(event) => setCompanySearch(event.target.value)}
                                                            disabled={companyLoading || invoiceSubmitting}
                                                        />
                                                    </label>
                                                    <div className="pos-order-invoice__search-actions">
                                                        <button type="submit" disabled={companyLoading || invoiceSubmitting}>Cerca</button>
                                                        <button
                                                            type="button"
                                                            onClick={handleCompanyReset}
                                                            disabled={(companyLoading && companySearch.length === 0) || invoiceSubmitting}
                                                        >
                                                            Azzera
                                                        </button>
                                                    </div>
                                                </form>
                                                {companyError && <p className="pos-order-invoice__error">{companyError}</p>}
                                                {companyLoading && <p className="pos-order-invoice__hint">Ricerca in corso…</p>}
                                                {!hasLinkedCompany && !companyLoading && !companyError && (
                                                    <p className="pos-order-invoice__hint">Collega un'anagrafica per abilitare l'invio della fattura.</p>
                                                )}
                                                <div className="pos-order-invoice__selector">
                                                    <label>
                                                        <span>Seleziona azienda</span>
                                                        <select
                                                            value={selectedCompanyId ?? ''}
                                                            onChange={handleCompanySelect}
                                                            disabled={companyLoading || invoiceSubmitting}
                                                        >
                                                            <option value="">Scegli un'azienda</option>
                                                            {companySelectOptions.map(option => (
                                                                <option key={option.id} value={option.id}>{option.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="pos-order-invoice__link-button"
                                                        onClick={handleLinkCompany}
                                                        disabled={linkDisabled}
                                                    >
                                                        Collega azienda
                                                    </button>
                                                </div>
                                                {(linkedCompanyName || linkedCompanyVat || invoiceInfo?.companyId) && (
                                                    <div className="pos-order-invoice__details">
                                                        <h5>Azienda collegata</h5>
                                                        <ul>
                                                            {linkedCompanyName && <li><strong>{linkedCompanyName}</strong></li>}
                                                            {linkedCompanyVat && <li>P.IVA {linkedCompanyVat}</li>}
                                                            {invoiceInfo?.companyId && <li>ID anagrafica {invoiceInfo.companyId}</li>}
                                                            {invoiceInfo?.providerMessage && !invoiceIssued && (
                                                                <li>{invoiceInfo.providerMessage}</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                                <div className="pos-order-invoice__footer">
                                                    <button type="button" onClick={handleIssueInvoice} disabled={issueDisabled}>
                                                        <FiPrinter aria-hidden="true" />
                                                        <span>{invoiceIssued ? 'Fattura inviata' : 'Emetti fattura'}</span>
                                                    </button>
                                                    {invoiceInfo?.documentReference && (
                                                        <p className="pos-order-invoice__ref">Riferimento: {invoiceInfo.documentReference}</p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <p className="pos-order-invoice__hint">Salva il conto per abilitare la fatturazione.</p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="pos-workspace__placeholder">Seleziona un ordine per gestire il totale.</p>
                    )}
                </div>
                {/* Fine colonna Totale */}
            </section>

            {paymentConfirmation && (
                <aside className="pos-payment-confirmation" role="status" aria-live="polite">
                    <span className="pos-payment-confirmation__icon" aria-hidden="true">
                        <FiCheckCircle />
                    </span>
                    <div className="pos-payment-confirmation__content">
                        <p className="pos-payment-confirmation__title">{paymentConfirmation.title}</p>
                        {paymentConfirmation.orderLabel && (
                            <p className="pos-payment-confirmation__meta">{paymentConfirmation.orderLabel}</p>
                        )}
                        {paymentConfirmation.subtitle && (
                            <p className="pos-payment-confirmation__subtitle">{paymentConfirmation.subtitle}</p>
                        )}
                        {paymentConfirmation.changeLabel && (
                            <p className="pos-payment-confirmation__meta">Resto {paymentConfirmation.changeLabel}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="pos-payment-confirmation__close"
                        onClick={dismissPaymentConfirmation}
                        aria-label="Chiudi notifica pagamento"
                    >
                        <FiX />
                    </button>
                </aside>
            )}

            {/* 🔧 Modali e pannelli ============================================ */}
            {adjustmentModal && currentAdjustmentMeta && (
                <div className="pos-modal" role="dialog" aria-modal="true">
                    <div className="pos-modal__dialog pos-modal__dialog--wide">
                        <header className="pos-modal__header pos-modal__header--row">
                            <div>
                                <h3>Correzioni conto</h3>
                                <p>
                                    <strong>{currentAdjustmentMeta.label}</strong>
                                    {' - '}
                                    {currentAdjustmentMeta.description}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="pos-modal__close"
                                onClick={closeAdjustmentModal}
                                aria-label="Chiudi correzioni"
                            >
                                <FiXCircle />
                            </button>
                        </header>
                        <nav className="pos-adjustment-tabs" aria-label="Tipi di correzione">
                            {ADJUSTMENT_TABS.map(tab => {
                                const meta = ADJUSTMENT_TAB_META[tab];
                                const isActive = adjustmentModal === tab;
                                const handleTabClick = () => {
                                    if (tab !== adjustmentModal) {
                                        openAdjustmentModal(tab);
                                    }
                                };
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={`pos-adjustment-tabs__button${isActive ? ' pos-adjustment-tabs__button--active' : ''}`}
                                        onClick={handleTabClick}
                                        aria-pressed={isActive}
                                    >
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </nav>
                        <div className="pos-adjustment__summary">
                            <div>
                                <span>Totale lordo</span>
                                <strong>{formatCurrency(paymentSummary.grossTotal)}</strong>
                            </div>
                            <div>
                                <span>Totale netto</span>
                                <strong>{paymentSummary.totalFormatted}</strong>
                            </div>
                            <div>
                                <span>Sconto attuale</span>
                                <strong>{paymentSummary.discountAmount > 0 ? `-${paymentSummary.discountFormatted}` : '—'}</strong>
                            </div>
                            <div>
                                <span>Buoni pasto</span>
                                <strong>{paymentSummary.voucherAmount > 0 ? `-${paymentSummary.voucherFormatted}` : '—'}</strong>
                            </div>
                        </div>
                        <form className="pos-modal__form pos-adjustment__form" onSubmit={handleAdjustmentSubmit}>
                            {adjustmentModal === 'customTotal' && (
                                <>
                                    <label className="pos-modal__field">
                                        <span>Nuovo totale finale</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder={paymentSummary.totalFormatted}
                                            value={adjustmentForm.customTotal}
                                            onChange={(event) => setAdjustmentForm(prev => ({ ...prev, customTotal: event.target.value }))}
                                            autoFocus
                                        />
                                    </label>
                                    <p className="pos-adjustment__hint">
                                        Inserisci l'importo desiderato; lo sconto verrà impostato automaticamente in base alla differenza dal lordo attuale.
                                    </p>
                                </>
                            )}
                            {adjustmentModal === 'discount' && (
                                <>
                                    <div className="pos-adjustment__group">
                                        <label className="pos-modal__field">
                                            <span>Modalità</span>
                                            <select
                                                value={adjustmentForm.discountMode}
                                                onChange={(event) => setAdjustmentForm(prev => ({
                                                    ...prev,
                                                    discountMode: event.target.value as 'amount' | 'percentage',
                                                }))}
                                            >
                                                <option value="amount">Importo (EUR)</option>
                                                <option value="percentage">Percentuale (%)</option>
                                            </select>
                                        </label>
                                        <label className="pos-modal__field">
                                            <span>{adjustmentForm.discountMode === 'percentage' ? 'Percentuale' : 'Importo'}</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                placeholder={adjustmentForm.discountMode === 'percentage' ? '0' : '0,00'}
                                                value={adjustmentForm.discountValue}
                                                onChange={(event) => setAdjustmentForm(prev => ({ ...prev, discountValue: event.target.value }))}
                                                autoFocus
                                            />
                                        </label>
                                    </div>
                                    <p className="pos-adjustment__hint">
                                        L'importo sconto non può superare il totale lordo. Le percentuali vengono arrotondate a due decimali.
                                    </p>
                                </>
                            )}
                            {adjustmentModal === 'voucher' && (
                                <div className="pos-adjustment__group pos-adjustment__group--split">
                                    <label className="pos-modal__field">
                                        <span>Numero buoni</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="0"
                                            value={adjustmentForm.voucherCount}
                                            onChange={(event) => setAdjustmentForm(prev => ({ ...prev, voucherCount: event.target.value }))}
                                            autoFocus
                                        />
                                    </label>
                                    <label className="pos-modal__field">
                                        <span>Valore unitario</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0,00"
                                            value={adjustmentForm.voucherValue}
                                            onChange={(event) => setAdjustmentForm(prev => ({ ...prev, voucherValue: event.target.value }))}
                                        />
                                    </label>
                                    {voucherPreviewAmount && (
                                        <p className="pos-adjustment__hint">
                                            Totale buoni: <strong>{voucherPreviewAmount}</strong>
                                        </p>
                                    )}
                                    <p className="pos-adjustment__hint">
                                        Inserisci quantità e valore per registrare i buoni riscossi; il sistema calcolerà l'importo totale.
                                    </p>
                                </div>
                            )}
                            {adjustmentModal === 'split' && (
                                <>
                                    <label className="pos-modal__field">
                                        <span>Numero di persone</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="2"
                                            value={adjustmentForm.splitShares}
                                            onChange={(event) => setAdjustmentForm(prev => ({ ...prev, splitShares: event.target.value }))}
                                            autoFocus
                                        />
                                    </label>
                                    {splitPreviewAmount && (
                                        <p className="pos-adjustment__hint">
                                            Quota stimata per persona: <strong>{splitPreviewAmount}</strong>
                                        </p>
                                    )}
                                    <p className="pos-adjustment__hint">
                                        Il totale netto viene diviso in parti uguali con arrotondamento a due decimali.
                                    </p>
                                </>
                            )}
                            {adjustmentError && <p className="pos-modal__error">{adjustmentError}</p>}
                            <div className="pos-modal__actions">
                                <button
                                    type="button"
                                    className="pos-modal__button pos-modal__button--ghost"
                                    onClick={closeAdjustmentModal}
                                >
                                    Annulla
                                </button>
                                <button type="submit" className="pos-modal__button">
                                    Applica
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {customItemModalOpen && (
                <div className="pos-modal" role="dialog" aria-modal="true">
                    <div className="pos-modal__dialog">
                        <header className="pos-modal__header">
                            <h3>Articolo personalizzato</h3>
                            <p>Crea un articolo una tantum con nome e prezzo liberi.</p>
                        </header>
                        <form className="pos-modal__form" onSubmit={handleCustomItemSubmit}>
                            <label className="pos-modal__field">
                                <span>Nome articolo</span>
                                <input
                                    type="text"
                                    value={customItemForm.name}
                                    onChange={(event) => {
                                        setCustomItemForm(prev => ({ ...prev, name: event.target.value }));
                                        setCustomItemError(null);
                                    }}
                                    placeholder="Articolo personalizzato"
                                    maxLength={80}
                                />
                            </label>
                            <label className="pos-modal__field">
                                <span>Prezzo unitario *</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0,00"
                                    value={customItemForm.price}
                                    onChange={(event) => {
                                        setCustomItemForm(prev => ({ ...prev, price: event.target.value }));
                                        setCustomItemError(null);
                                    }}
                                    required
                                />
                            </label>
                            <label className="pos-modal__field">
                                <span>Quantità</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={customItemForm.quantity}
                                    onChange={(event) => {
                                        setCustomItemForm(prev => ({ ...prev, quantity: event.target.value }));
                                        setCustomItemError(null);
                                    }}
                                />
                            </label>
                            {customItemError && <p className="pos-modal__error">{customItemError}</p>}
                            <div className="pos-modal__actions">
                                <button type="button" className="pos-modal__button pos-modal__button--ghost" onClick={closeCustomItemModal}>
                                    Annulla
                                </button>
                                <button type="submit" className="pos-modal__button">
                                    Aggiungi al conto
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 🍽️ Modale Coperto */}
            {coverChargeModalOpen && (
                <div className="pos-modal" role="dialog" aria-modal="true">
                    <div className="pos-modal__dialog" style={{ maxWidth: '400px' }}>
                        <header className="pos-modal__header">
                            <h3>🍽️ Coperto</h3>
                            <p>Quante persone al tavolo?</p>
                        </header>
                        <div className="pos-modal__form" style={{ padding: '24px' }}>
                            <label className="pos-modal__field">
                                <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>Numero persone</span>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    min="0"
                                    max="99"
                                    value={numberOfPeople}
                                    onChange={(e) => setNumberOfPeople(e.target.value)}
                                    style={{
                                        fontSize: '2rem',
                                        textAlign: 'center',
                                        padding: '16px',
                                        fontWeight: 'bold'
                                    }}
                                    autoFocus
                                />
                            </label>
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: '#f3f4f6',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>
                                    Totale coperto
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                                    {formatCurrency((parseInt(numberOfPeople) || 0) * coverChargeAmount)}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '4px' }}>
                                    ({formatCurrency(coverChargeAmount)} × {numberOfPeople || 0} {parseInt(numberOfPeople) === 1 ? 'persona' : 'persone'})
                                </div>
                            </div>
                            {customItemError && (
                                <div className="pos-modal__error" role="alert">
                                    {customItemError}
                                </div>
                            )}
                            <div className="pos-modal__actions" style={{ marginTop: '24px' }}>
                                <button
                                    type="button"
                                    className="pos-modal__btn pos-modal__btn--secondary"
                                    onClick={handleCoverChargeSkip}
                                >
                                    Salta
                                </button>
                                <button
                                    type="button"
                                    className="pos-modal__btn pos-modal__btn--primary"
                                    onClick={handleCoverChargeConfirm}
                                    disabled={!numberOfPeople || parseInt(numberOfPeople) <= 0}
                                >
                                    ✓ Conferma coperto
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {manualModal && (
                <div className="pos-modal" role="dialog" aria-modal="true">
                    <div className="pos-modal__dialog">
                        <header className="pos-modal__header">
                            <h3>{manualModal.title}</h3>
                            <p>{manualModal.description}</p>
                        </header>
                        <form className="pos-modal__form" onSubmit={handleManualSubmit}>
                            <label className="pos-modal__field">
                                <span>Importo *</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0,00"
                                    value={manualForm.amount}
                                    onChange={(event) => setManualForm(prev => ({ ...prev, amount: event.target.value }))}
                                    required
                                />
                            </label>
                            <label className="pos-modal__field">
                                <span>Riferimento (facoltativo)</span>
                                <input
                                    type="text"
                                    value={manualForm.reference}
                                    onChange={(event) => setManualForm(prev => ({ ...prev, reference: event.target.value }))}
                                    maxLength={160}
                                />
                            </label>
                            <label className="pos-modal__field">
                                <span>Note (facoltative)</span>
                                <textarea
                                    value={manualForm.note}
                                    onChange={(event) => setManualForm(prev => ({ ...prev, note: event.target.value }))}
                                    maxLength={500}
                                    rows={3}
                                />
                            </label>
                            {manualError && <p className="pos-modal__error">{manualError}</p>}
                            <div className="pos-modal__actions">
                                <button type="button" className="pos-modal__button pos-modal__button--ghost" onClick={closeManualModal}>
                                    Annulla
                                </button>
                                <button type="submit" className="pos-modal__button" disabled={manualSubmitting}>
                                    {manualSubmitting ? 'Registrazione…' : 'Registra incasso'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showKeypad && keypadMode && (
                <div className="pos-modal pos-modal--keypad" role="dialog" aria-modal="true">
                    <div className="pos-modal__dialog pos-modal__dialog--keypad">
                        <header className="pos-modal__header">
                            <h3>
                                {keypadMode === 'cash' && '💵 Inserisci importo contanti'}
                                {keypadMode === 'card' && '💳 Inserisci importo carta'}
                                {keypadMode === 'custom' && 'Importo personalizzato'}
                            </h3>
                            <button
                                type="button"
                                className="pos-modal__close"
                                onClick={() => {
                                    setShowKeypad(false);
                                    setKeypadMode(null);
                                }}
                                aria-label="Chiudi"
                            >
                                <FiXCircle />
                            </button>
                        </header>
                        <div className="pos-keypad pos-keypad--modal">
                            <div className="pos-keypad__display pos-keypad__display--large">
                                <small>Importo inserito</small>
                                <strong>{paymentSummary.inputFormatted}</strong>
                                <small className="pos-keypad__hint">
                                    Da incassare: {paymentSummary.totalFormatted} • Resto: {paymentSummary.remainingFormatted}
                                </small>
                            </div>
                            <div className="pos-keypad__body">
                                <div className="pos-keypad__grid">
                                    {keypadRows.map((row, rowIndex) => (
                                        <div key={`row-${rowIndex}`} className="pos-keypad__row">
                                            {row.map(key => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className="pos-keypad__button"
                                                    onClick={() => handleKeypadPress(key)}
                                                >
                                                    {key === '.' ? ',' : key}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                                <div className="pos-keypad__utilities">
                                    {keypadUtilities.map(key => (
                                        <button
                                            key={key}
                                            type="button"
                                            className="pos-keypad__button pos-keypad__button--utility"
                                            onClick={() => handleKeypadPress(key)}
                                        >
                                            {key}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="pos-keypad__actions">
                                <button
                                    type="button"
                                    className="pos-modal__button pos-modal__button--ghost"
                                    onClick={() => {
                                        setShowKeypad(false);
                                        setKeypadMode(null);
                                        setPaymentInput('');
                                    }}
                                >
                                    Annulla
                                </button>
                                <button
                                    type="button"
                                    className="pos-modal__button"
                                    onClick={() => {
                                        if (keypadMode === 'cash') {
                                            handlePaymentAction('cash');
                                        } else if (keypadMode === 'card') {
                                            handlePaymentAction('pos');
                                        }
                                        setShowKeypad(false);
                                        setKeypadMode(null);
                                    }}
                                    disabled={orderSubmitting || paymentSummary.paymentAmount <= 0}
                                >
                                    {keypadMode === 'cash' ? '💵 Conferma contanti' : '💳 Conferma carta'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            <PaymentModal
                isOpen={paymentModalOpen}
                onClose={() => {
                    setPaymentModalOpen(false);
                    setPendingPaymentAction(null);
                }}
                totalAmount={paymentSummary.netTotal}
                onConfirmPayment={handleConfirmPayment}
                title="Tipo di pagamento"
                currency="€"
                initialMethod={pendingPaymentAction === 'pos' ? 'POS' : 'CASH'} // 🔧 Passa il metodo corretto
            />
        </div>
    );
};

export default PosPageFull;
